import React, { useState, useEffect, useCallback, useContext } from 'react';
import { ThemeContext } from '../../context/ThemeContext';
import { LoadingState } from '../../components/common/LoadingState';
import { Card, Row, Col, Badge, Modal, Form, Button, Table, Tabs, Tab, ProgressBar } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axiosInstance from '../../api/axios';
import { Endpoints } from '../../api/Endpoints';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    PieChart,
    Pie,
    Cell,
    CartesianGrid
} from 'recharts';

// Rutex Corporate Colors
const BRAND_NAVY = '#00024f';
const BRAND_BLUE = '#183eeb';

const MARGIN_COLORS = {
    'Alto': '#059669',
    'En el rango': '#0284c7',
    'Bajo': '#ef4444',
    'No reportado': '#94a3b8'
};

const HOTSPOT_CONFIG = [
    { key: 'cabecera_gondola', labelKey: 'hotspot_cabecera_gondola', shortLabel: 'Cabecera', label: 'Cabecera de Góndola', icon: 'mdi-view-headline', color: BRAND_BLUE },
    { key: 'linea_caja', labelKey: 'hotspot_linea_caja', shortLabel: 'Caja', label: 'Línea de Caja', icon: 'mdi-cash-register', color: '#8b5cf6' },
    { key: 'nivel_ojos', labelKey: 'hotspot_nivel_ojos', shortLabel: 'Nivel Ojos', label: 'Nivel de Ojos (Zona Dorada)', icon: 'mdi-eye-check-outline', color: '#0284c7' },
    { key: 'isla_promocional', labelKey: 'hotspot_isla_promocional', shortLabel: 'Isla', label: 'Isla Promocional', icon: 'mdi-island', color: '#f59e0b' }
];

const MerchandisingReports = () => {
    const { t } = useTranslation();
    const { isDark } = useContext(ThemeContext) || {};

    // Loading & Data States
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [reports, setReports] = useState([]);
    const [kpis, setKpis] = useState({
        total_auditorias: 0,
        total_pdvs: 0,
        total_quiebres: 0,
        cobertura_fotos_porcentaje: 0,
        pop_buen_estado_porcentaje: 0
    });
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 12,
        total: 0
    });

    // View Mode: 'gallery' | 'table' | 'analytics'
    const [viewMode, setViewMode] = useState('gallery');

    // Filter States
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [selectedMerchId, setSelectedMerchId] = useState('');
    const [selectedZoneId, setSelectedZoneId] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Catalog States for Selectors
    const [merchandisers, setMerchandisers] = useState([]);
    const [zones, setZones] = useState([]);

    // Detail Modal State
    const [selectedReport, setSelectedReport] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);

    // Image Zoom / Lightbox State
    const [zoomImage, setZoomImage] = useState(null);

    // Analytics Data State (Market Intelligence & Visibility)
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loadingAnalytics, setLoadingAnalytics] = useState(false);
    const [competitorSearch, setCompetitorSearch] = useState('');

    // DrillDown Modal State
    const [drillDownModal, setDrillDownModal] = useState({
        show: false,
        title: '',
        subtitle: '',
        badgeText: '',
        badgeVariant: 'primary',
        items: [],
        type: 'generic'
    });

    const handleOpenDrillDown = (title, items = [], options = {}) => {
        setDrillDownModal({
            show: true,
            title,
            subtitle: options.subtitle || t('drilldown_subtitle', 'Trazabilidad directa al reporte individual relevado por el mercaderista'),
            badgeText: options.badgeText || `${items?.length || 0} PDVs`,
            badgeVariant: options.badgeVariant || 'primary',
            items: Array.isArray(items) ? items : [],
            type: options.type || 'generic'
        });
    };

    const handleOpenReportById = async (reportId) => {
        if (!reportId) return;
        const existing = reports.find(r => r.id === reportId);
        if (existing) {
            setSelectedReport(existing);
            setShowDetailModal(true);
            return;
        }

        try {
            const endpoint = Endpoints.MERCHANDISING_REPORT_BY_ID 
                ? Endpoints.MERCHANDISING_REPORT_BY_ID(reportId) 
                : `/audit/merchandising/reports/${reportId}`;
            const res = await axiosInstance.get(endpoint);
            if (res.data?.success && res.data?.data) {
                setSelectedReport(res.data.data);
                setShowDetailModal(true);
            } else {
                toast.error(t('report_not_found', 'No se pudo cargar el reporte de auditoría'));
            }
        } catch (err) {
            console.error('Error fetching report by ID:', err);
            toast.error(t('report_not_found', 'No se pudo cargar el reporte de auditoría'));
        }
    };

    // Fetch Catalogs (Merchandisers & Zones)
    useEffect(() => {
        const loadCatalogs = async () => {
            try {
                const [merchRes, zonesRes] = await Promise.all([
                    axiosInstance.get(Endpoints.MERCHANDISERS || '/admin/merchandisers').catch(() => ({ data: { data: [] } })),
                    axiosInstance.get(Endpoints.ZONES || '/admin/zones').catch(() => ({ data: { data: [] } }))
                ]);
                setMerchandisers(Array.isArray(merchRes.data?.data) ? merchRes.data.data : []);
                setZones(Array.isArray(zonesRes.data?.data) ? zonesRes.data.data : []);
            } catch (err) {
                console.error('Error cargando catálogos de mercaderistas/zonas:', err);
            }
        };
        loadCatalogs();
    }, []);

    // Fetch Reports
    const fetchReports = useCallback(async (page = 1, isRefresh = false) => {
        if (isRefresh) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }

        try {
            const params = new URLSearchParams();
            params.append('page', page);
            params.append('per_page', viewMode === 'gallery' ? 12 : 20);
            if (dateFrom) params.append('date_from', dateFrom);
            if (dateTo) params.append('date_to', dateTo);
            if (selectedMerchId) params.append('vendedor_id', selectedMerchId);
            if (selectedZoneId) params.append('zone_id', selectedZoneId);
            if (searchTerm.trim()) params.append('search', searchTerm.trim());

            const endpoint = `${Endpoints.MERCHANDISING_REPORTS || '/audit/merchandising/reports'}?${params.toString()}`;
            const res = await axiosInstance.get(endpoint);

            if (res.data?.success) {
                setReports(res.data.data || []);
                if (res.data.kpis) {
                    setKpis(res.data.kpis);
                }
                if (res.data.meta) {
                    setPagination(res.data.meta);
                }
            }
        } catch (error) {
            console.error('Error al cargar reportes de auditoría:', error);
            toast.error(t('error_loading_reports', 'Error al consultar reportes de auditoría.'));
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [dateFrom, dateTo, selectedMerchId, selectedZoneId, searchTerm, viewMode, t]);

    // Fetch Analytics Data
    const fetchAnalytics = useCallback(async (isRefresh = false) => {
        if (isRefresh) {
            setRefreshing(true);
        } else {
            setLoadingAnalytics(true);
        }

        try {
            const params = new URLSearchParams();
            if (dateFrom) params.append('date_from', dateFrom);
            if (dateTo) params.append('date_to', dateTo);
            if (selectedMerchId) params.append('vendedor_id', selectedMerchId);
            if (selectedZoneId) params.append('zone_id', selectedZoneId);
            if (searchTerm.trim()) params.append('search', searchTerm.trim());

            const endpoint = `${Endpoints.MERCHANDISING_ANALYTICS || '/audit/merchandising/analytics'}?${params.toString()}`;
            const res = await axiosInstance.get(endpoint);

            if (res.data?.success) {
                setAnalyticsData(res.data);
            }
        } catch (error) {
            console.error('Error al cargar analítica de mercadeo:', error);
            toast.error(t('error_loading_analytics', 'Error al cargar analítica de mercado y visibilidad.'));
        } finally {
            setLoadingAnalytics(false);
            setRefreshing(false);
        }
    }, [dateFrom, dateTo, selectedMerchId, selectedZoneId, searchTerm, t]);

    useEffect(() => {
        if (viewMode === 'analytics') {
            fetchAnalytics();
        } else {
            fetchReports(pagination.current_page);
        }
    }, [viewMode, fetchReports, fetchAnalytics, pagination.current_page]);

    const handleClearFilters = () => {
        setDateFrom('');
        setDateTo('');
        setSelectedMerchId('');
        setSelectedZoneId('');
        setSearchTerm('');
    };

    const handleRefresh = () => {
        if (viewMode === 'analytics') {
            fetchAnalytics(true);
        } else {
            fetchReports(pagination.current_page, true);
        }
    };

    // Helper: Margen Percibido Badge (Rutex Soft Badge Style)
    const renderMarginBadge = (margin) => {
        if (!margin) {
            return (
                <span 
                    className="badge rounded-pill d-inline-flex align-items-center gap-1"
                    style={{ 
                        backgroundColor: isDark ? 'rgba(148, 163, 184, 0.15)' : 'rgba(100, 116, 139, 0.1)', 
                        color: isDark ? '#cbd5e1' : '#64748b', 
                        border: isDark ? '1px solid rgba(148, 163, 184, 0.3)' : '1px solid #cbd5e1', 
                        fontSize: '0.74rem', 
                        padding: '4px 9px',
                        fontWeight: '500'
                    }}
                >
                    <i className="mdi mdi-help-circle-outline"></i> {t('margin_none', 'No reportado')}
                </span>
            );
        }
        if (margin === 'Alto') {
            return (
                <span 
                    className="badge rounded-pill d-inline-flex align-items-center gap-1"
                    style={{ 
                        backgroundColor: isDark ? 'rgba(16, 185, 129, 0.22)' : 'rgba(16, 185, 129, 0.12)', 
                        color: isDark ? '#6ee7b7' : '#059669', 
                        border: isDark ? '1px solid rgba(110, 231, 183, 0.45)' : '1px solid rgba(16, 185, 129, 0.3)', 
                        fontSize: '0.74rem', 
                        padding: '4px 9px',
                        fontWeight: '600'
                    }}
                >
                    <i className="mdi mdi-trending-up"></i> {t('margin_high', 'Alto')}
                </span>
            );
        }
        if (margin === 'En el rango') {
            return (
                <span 
                    className="badge rounded-pill d-inline-flex align-items-center gap-1"
                    style={{ 
                        backgroundColor: isDark ? 'rgba(2, 132, 199, 0.22)' : 'rgba(2, 132, 199, 0.12)', 
                        color: isDark ? '#38bdf8' : '#0284c7', 
                        border: isDark ? '1px solid rgba(56, 189, 248, 0.45)' : '1px solid rgba(2, 132, 199, 0.3)', 
                        fontSize: '0.74rem', 
                        padding: '4px 9px',
                        fontWeight: '600'
                    }}
                >
                    <i className="mdi mdi-scale-balance"></i> {t('margin_in_range', 'En el rango')}
                </span>
            );
        }
        if (margin === 'Bajo') {
            return (
                <span 
                    className="badge rounded-pill d-inline-flex align-items-center gap-1"
                    style={{ 
                        backgroundColor: isDark ? 'rgba(239, 68, 68, 0.22)' : 'rgba(239, 68, 68, 0.12)', 
                        color: isDark ? '#fca5a5' : '#dc2626', 
                        border: isDark ? '1px solid rgba(252, 165, 165, 0.45)' : '1px solid rgba(239, 68, 68, 0.3)', 
                        fontSize: '0.74rem', 
                        padding: '4px 9px',
                        fontWeight: '600'
                    }}
                >
                    <i className="mdi mdi-trending-down"></i> {t('margin_low', 'Bajo')}
                </span>
            );
        }
        return <Badge bg="secondary" className="px-2 py-1">{margin}</Badge>;
    };

    // Helper: Check if a hotspot is present in report
    const hasHotspot = (puntosCalientes, spotKey) => {
        if (!puntosCalientes) return false;
        if (Array.isArray(puntosCalientes)) {
            return puntosCalientes.includes(spotKey);
        }
        if (typeof puntosCalientes === 'object') {
            return Boolean(puntosCalientes[spotKey]);
        }
        return false;
    };

    // Helper: Render Hotspots Summary Chips (Rutex Soft Badge Style)
    const renderHotspotsBadges = (puntosCalientes) => {
        const activeSpots = HOTSPOT_CONFIG.filter(cfg => hasHotspot(puntosCalientes, cfg.key));
        if (activeSpots.length === 0) {
            return <span className="small text-muted">{t('no_hotspots', 'Sin puntos calientes')}</span>;
        }
        return (
            <div className="d-flex flex-wrap gap-1">
                {activeSpots.map(spot => (
                    <span 
                        key={spot.key} 
                        className="badge rounded-pill d-inline-flex align-items-center gap-1"
                        style={{ 
                            backgroundColor: isDark ? 'rgba(24, 62, 235, 0.2)' : 'rgba(24, 62, 235, 0.08)', 
                            color: isDark ? '#93c5fd' : BRAND_BLUE, 
                            border: isDark ? '1px solid rgba(147, 197, 253, 0.4)' : '1px solid rgba(24, 62, 235, 0.25)', 
                            fontSize: '0.72rem', 
                            padding: '3px 7px',
                            fontWeight: '600'
                        }}
                    >
                        <i className={`mdi ${spot.icon}`}></i>
                        {spot.shortLabel || spot.label}
                    </span>
                ))}
            </div>
        );
    };

    // Helper: POP Badge (Rutex Soft Badge Style)
    const renderPopBadge = (pop) => {
        if (!pop) {
            return (
                <span 
                    className="badge rounded-pill d-inline-flex align-items-center gap-1"
                    style={{ 
                        backgroundColor: isDark ? 'rgba(148, 163, 184, 0.15)' : 'rgba(100, 116, 139, 0.1)', 
                        color: isDark ? '#cbd5e1' : '#64748b', 
                        border: isDark ? '1px solid rgba(148, 163, 184, 0.3)' : '1px solid #cbd5e1', 
                        fontSize: '0.72rem', 
                        padding: '4px 9px',
                        fontWeight: '500'
                    }}
                >
                    <i className="mdi mdi-help-circle-outline"></i> {t('pop_no_data', 'Sin POP')}
                </span>
            );
        }
        const status = pop.exhibidor_principal || '';
        if (status === 'buen_estado') {
            return (
                <span 
                    className="badge rounded-pill d-inline-flex align-items-center gap-1"
                    style={{ 
                        backgroundColor: isDark ? 'rgba(16, 185, 129, 0.22)' : 'rgba(16, 185, 129, 0.12)', 
                        color: isDark ? '#6ee7b7' : '#059669', 
                        border: isDark ? '1px solid rgba(110, 231, 183, 0.45)' : '1px solid rgba(16, 185, 129, 0.3)', 
                        fontSize: '0.72rem', 
                        padding: '4px 9px',
                        fontWeight: '600'
                    }}
                >
                    <i className="mdi mdi-check-circle-outline"></i> {t('pop_ok', 'Exhibidor OK')}
                </span>
            );
        }
        if (status === 'danado') {
            return (
                <span 
                    className="badge rounded-pill d-inline-flex align-items-center gap-1"
                    style={{ 
                        backgroundColor: isDark ? 'rgba(239, 68, 68, 0.22)' : 'rgba(239, 68, 68, 0.12)', 
                        color: isDark ? '#fca5a5' : '#dc2626', 
                        border: isDark ? '1px solid rgba(252, 165, 165, 0.45)' : '1px solid rgba(239, 68, 68, 0.3)', 
                        fontSize: '0.72rem', 
                        padding: '4px 9px',
                        fontWeight: '600'
                    }}
                >
                    <i className="mdi mdi-alert-circle-outline"></i> {t('pop_damaged', 'Exhibidor Dañado')}
                </span>
            );
        }
        if (status === 'no_tiene') {
            return (
                <span 
                    className="badge rounded-pill d-inline-flex align-items-center gap-1"
                    style={{ 
                        backgroundColor: isDark ? 'rgba(148, 163, 184, 0.15)' : 'rgba(100, 116, 139, 0.1)', 
                        color: isDark ? '#cbd5e1' : '#64748b', 
                        border: isDark ? '1px solid rgba(148, 163, 184, 0.3)' : '1px solid #cbd5e1', 
                        fontSize: '0.72rem', 
                        padding: '4px 9px',
                        fontWeight: '500'
                    }}
                >
                    <i className="mdi mdi-close-circle-outline"></i> {t('pop_none', 'Sin Exhibidor')}
                </span>
            );
        }
        return <Badge bg="info" className="px-2 py-1">{status}</Badge>;
    };

    // Helper: Safely format dates without timezone shifts
    const formatVisitDate = (val) => {
        if (!val) return '-';
        if (typeof val === 'string') {
            const datePart = val.split('T')[0].split(' ')[0];
            if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
                const [y, m, d] = datePart.split('-');
                const parsed = new Date(Number(y), Number(m) - 1, Number(d));
                return isNaN(parsed.getTime()) ? datePart : parsed.toLocaleDateString();
            }
        }
        const d = new Date(val);
        return isNaN(d.getTime()) ? String(val) : d.toLocaleDateString();
    };

    const formatVisitTime = (val) => {
        if (!val) return '-';
        const d = new Date(val);
        return isNaN(d.getTime()) ? '-' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    // Corporate Design Theme Tokens
    const cardBg = isDark ? '#161e38' : '#ffffff';
    const cardBorder = isDark ? '#233052' : '#e2e8f0';
    const textColor = isDark ? '#ffffff' : '#010028';
    const textMutedColor = isDark ? '#94a3b8' : '#64748b';
    const subCardBg = isDark ? '#10172d' : '#f8faff';

    // Filter competitor table in Analytics view
    const filteredCompetitors = (analyticsData?.precios_competencia?.listado || []).filter(item => {
        if (!competitorSearch.trim()) return true;
        const s = competitorSearch.toLowerCase();
        return (
            (item.marca || '').toLowerCase().includes(s) ||
            (item.producto || '').toLowerCase().includes(s) ||
            (item.cliente || '').toLowerCase().includes(s) ||
            (item.zona || '').toLowerCase().includes(s)
        );
    });

    return (
        <div>
            <ToastContainer position="top-right" autoClose={3000} theme={isDark ? "dark" : "colored"} />

            {/* Rutex Corporate Page Header */}
            <div className="page-header d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                <div>
                    <h3 className="page-title mb-0" style={{ color: isDark ? '#ffffff' : '#010028', fontWeight: 'bold' }}>
                        <i className="mdi mdi-store-search me-2 text-primary"></i>
                        {t('merchandising_reports_title', 'Auditoría de Anaquel y Trade Marketing')}
                    </h3>
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb mb-0 mt-1">
                            <li className="breadcrumb-item"><a href="!#" onClick={e => e.preventDefault()}>Auditoría</a></li>
                            <li className="breadcrumb-item active" aria-current="page">Trade Marketing, Quiebres y Visibilidad</li>
                        </ol>
                    </nav>
                </div>

                <div className="d-flex align-items-center gap-2">
                    <Button 
                        variant="outline-primary" 
                        size="sm" 
                        onClick={handleRefresh}
                        disabled={refreshing || loadingAnalytics}
                        className="btn-icon-text border shadow-sm"
                        style={{ borderRadius: '6px', fontWeight: 'bold', padding: '7px 14px' }}
                    >
                        <i className={`mdi mdi-refresh btn-icon-prepend ${refreshing || loadingAnalytics ? 'mdi-spin' : ''}`}></i>
                        <span>{t('refresh', 'Actualizar')}</span>
                    </Button>
                </div>
            </div>

            {/* Rutex KPI Summary Cards */}
            <Row className="g-3 mb-4">
                <Col xs={12} sm={6} md={4} lg>
                    <Card className="h-100 shadow-sm border-0" style={{ backgroundColor: cardBg, borderRadius: '12px', borderLeft: `4px solid ${BRAND_BLUE}` }}>
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="small fw-bold text-uppercase" style={{ color: textMutedColor, letterSpacing: '0.5px' }}>
                                    {t('kpi_total_audits', 'Auditorías')}
                                </span>
                                <div className="p-2 rounded-circle" style={{ backgroundColor: isDark ? 'rgba(24, 62, 235, 0.2)' : 'rgba(24, 62, 235, 0.1)', color: BRAND_BLUE }}>
                                    <i className="mdi mdi-clipboard-check-outline fs-5"></i>
                                </div>
                            </div>
                            <h3 className="fw-bold mb-0" style={{ color: textColor }}>{kpis.total_auditorias}</h3>
                            <span className="small text-muted">{t('kpi_total_audits_hint', 'Reportes recibidos')}</span>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12} sm={6} md={4} lg>
                    <Card className="h-100 shadow-sm border-0" style={{ backgroundColor: cardBg, borderRadius: '12px', borderLeft: '4px solid #0284c7' }}>
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="small fw-bold text-uppercase" style={{ color: textMutedColor, letterSpacing: '0.5px' }}>
                                    {t('kpi_audited_pdvs', 'PDVs Auditados')}
                                </span>
                                <div className="p-2 rounded-circle" style={{ backgroundColor: isDark ? 'rgba(2, 132, 199, 0.2)' : 'rgba(2, 132, 199, 0.1)', color: '#0284c7' }}>
                                    <i className="mdi mdi-store-outline fs-5"></i>
                                </div>
                            </div>
                            <h3 className="fw-bold mb-0" style={{ color: textColor }}>{kpis.total_pdvs}</h3>
                            <span className="small text-muted">{t('kpi_audited_pdvs_hint', 'Puntos de venta')}</span>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12} sm={6} md={4} lg>
                    <Card className="h-100 shadow-sm border-0" style={{ backgroundColor: cardBg, borderRadius: '12px', borderLeft: '4px solid #ef4444' }}>
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="small fw-bold text-uppercase" style={{ color: textMutedColor, letterSpacing: '0.5px' }}>
                                    {t('kpi_stock_alerts', 'Quiebres Stock')}
                                </span>
                                <div className="p-2 rounded-circle" style={{ backgroundColor: isDark ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>
                                    <i className="mdi mdi-alert-decagram-outline fs-5"></i>
                                </div>
                            </div>
                            <h3 className="fw-bold mb-0 text-danger">{kpis.total_quiebres}</h3>
                            <span className="small text-muted">{t('kpi_stock_alerts_hint', 'Agotados en anaquel')}</span>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12} sm={6} md={4} lg>
                    <Card className="h-100 shadow-sm border-0" style={{ backgroundColor: cardBg, borderRadius: '12px', borderLeft: '4px solid #059669' }}>
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="small fw-bold text-uppercase" style={{ color: textMutedColor, letterSpacing: '0.5px' }}>
                                    {t('kpi_pop_good_condition', 'Exhibidores OK')}
                                </span>
                                <div className="p-2 rounded-circle" style={{ backgroundColor: isDark ? 'rgba(5, 150, 105, 0.2)' : 'rgba(5, 150, 105, 0.1)', color: '#059669' }}>
                                    <i className="mdi mdi-store-check fs-5"></i>
                                </div>
                            </div>
                            <h3 className="fw-bold mb-0 text-success">{kpis.pop_buen_estado_porcentaje ?? 0}%</h3>
                            <span className="small text-muted">{t('kpi_pop_good_condition_hint', 'POP en buen estado')}</span>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12} sm={6} md={4} lg>
                    <Card className="h-100 shadow-sm border-0" style={{ backgroundColor: cardBg, borderRadius: '12px', borderLeft: '4px solid #8b5cf6' }}>
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="small fw-bold text-uppercase" style={{ color: textMutedColor, letterSpacing: '0.5px' }}>
                                    {t('kpi_photo_coverage', 'Fotos Antes/Desp.')}
                                </span>
                                <div className="p-2 rounded-circle" style={{ backgroundColor: isDark ? 'rgba(139, 92, 246, 0.2)' : 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>
                                    <i className="mdi mdi-camera-burst fs-5"></i>
                                </div>
                            </div>
                            <h3 className="fw-bold mb-0" style={{ color: '#8b5cf6' }}>{kpis.cobertura_fotos_porcentaje}%</h3>
                            <span className="small text-muted">{t('kpi_photo_coverage_hint', 'Evidencia fotográfica')}</span>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Filter Toolbar (Rutex Corporate Card & Form Controls) */}
            <Card className="shadow-sm mb-4 border-0" style={{ borderRadius: '12px', backgroundColor: cardBg, border: isDark ? '1px solid #233052' : 'none' }}>
                <Card.Body className="p-3">
                    <Row className="g-3 align-items-end">
                        <Col xs={12} sm={6} md={3} lg={2}>
                            <Form.Label className="small fw-bold text-muted mb-1">
                                {t('filter_from', 'Desde')}
                            </Form.Label>
                            <Form.Control 
                                type="date" 
                                value={dateFrom} 
                                onChange={(e) => setDateFrom(e.target.value)}
                                className="customer-filter-control w-100 shadow-none"
                            />
                        </Col>

                        <Col xs={12} sm={6} md={3} lg={2}>
                            <Form.Label className="small fw-bold text-muted mb-1">
                                {t('filter_to', 'Hasta')}
                            </Form.Label>
                            <Form.Control 
                                type="date" 
                                value={dateTo} 
                                onChange={(e) => setDateTo(e.target.value)}
                                className="customer-filter-control w-100 shadow-none"
                            />
                        </Col>

                        <Col xs={12} sm={6} md={3} lg={3}>
                            <Form.Label className="small fw-bold text-muted mb-1">
                                {t('filter_merchandiser', 'Mercaderista')}
                            </Form.Label>
                            <Form.Select 
                                value={selectedMerchId} 
                                onChange={(e) => setSelectedMerchId(e.target.value)}
                                className="customer-filter-control w-100 cursor-pointer shadow-none"
                            >
                                <option value="">{t('all_merchandisers', 'Todos los Mercaderistas')}</option>
                                {merchandisers.map(m => (
                                    <option key={m.id} value={m.id}>{m.nombre_completo || m.email}</option>
                                ))}
                            </Form.Select>
                        </Col>

                        <Col xs={12} sm={6} md={3} lg={2}>
                            <Form.Label className="small fw-bold text-muted mb-1">
                                {t('filter_zone', 'Zona')}
                            </Form.Label>
                            <Form.Select 
                                value={selectedZoneId} 
                                onChange={(e) => setSelectedZoneId(e.target.value)}
                                className="customer-filter-control w-100 cursor-pointer shadow-none"
                            >
                                <option value="">{t('all_zones', 'Todas las Zonas')}</option>
                                {zones.map(z => (
                                    <option key={z.id} value={z.id}>{z.name} {z.code ? `(${z.code})` : ''}</option>
                                ))}
                            </Form.Select>
                        </Col>

                        <Col xs={12} md lg>
                            <Form.Label className="small fw-bold text-muted mb-1">
                                {t('search_pdv_rif', 'Buscar PDV / RIF')}
                            </Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Control 
                                    type="text" 
                                    placeholder={t('search_merchandising_placeholder', 'Buscar por PDV, RIF o mercaderista...')}
                                    value={searchTerm} 
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="customer-filter-control w-100 shadow-none"
                                />
                                {(dateFrom || dateTo || selectedMerchId || selectedZoneId || searchTerm) && (
                                    <Button 
                                        variant="outline-secondary" 
                                        onClick={handleClearFilters}
                                        title={t('clear_filters', 'Limpiar filtros')}
                                        className="d-flex align-items-center justify-content-center px-3"
                                        style={{ height: '42px', borderRadius: '8px', flexShrink: 0 }}
                                    >
                                        <i className="mdi mdi-close fs-5"></i>
                                    </Button>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Central Navigation Tabs using Rutex "minimal-underline-tabs" */}
            <Tabs 
                activeKey={viewMode} 
                onSelect={(k) => setViewMode(k)} 
                className="minimal-underline-tabs mb-4"
            >
                {/* TAB 1: GALERÍA VISUAL */}
                <Tab 
                    eventKey="gallery" 
                    title={
                        <span className="d-flex align-items-center gap-1">
                            <i className="mdi mdi-view-grid"></i>
                            <span>{t('view_gallery', 'Galería Visual')}</span>
                            <Badge bg="primary" className="ms-1" style={{ fontSize: '0.72rem' }}>
                                {pagination.total || reports.length}
                            </Badge>
                        </span>
                    }
                >
                    {loading ? (
                        <LoadingState message={t('loading_reports', 'Cargando auditorías de anaquel...')} />
                    ) : reports.length === 0 ? (
                        <Card className="text-center p-5 shadow-sm border-0" style={{ backgroundColor: cardBg, borderRadius: '12px' }}>
                            <Card.Body>
                                <i className="mdi mdi-file-search-outline fs-1 text-muted mb-2"></i>
                                <h5 className="fw-bold" style={{ color: textColor }}>{t('no_reports_found', 'No se encontraron auditorías')}</h5>
                                <p className="text-muted small mb-3">{t('no_reports_found_hint', 'No hay reportes de mercaderistas que coincidan con los filtros seleccionados.')}</p>
                                <Button variant="outline-primary" size="sm" onClick={handleClearFilters} style={{ borderRadius: '6px' }}>
                                    <i className="mdi mdi-filter-remove me-1"></i>{t('reset_filters', 'Restablecer filtros')}
                                </Button>
                            </Card.Body>
                        </Card>
                    ) : (
                        <Row className="g-4">
                            {reports.map((item) => {
                                const cliente = item.visit?.cliente;
                                const vendedor = item.vendedor;
                                const visit = item.visit;
                                const pop = item.material_pop;
                                const inventario = Array.isArray(item.inventario_pdv) ? item.inventario_pdv : [];
                                const quiebres = Array.isArray(item.quiebres_stock) ? item.quiebres_stock : [];
                                const margen = item.margen_percibido;
                                const puntos = item.puntos_calientes;

                                return (
                                    <Col xs={12} md={6} xl={4} key={item.id}>
                                        <Card className="h-100 shadow-sm border-0" style={{ backgroundColor: cardBg, borderRadius: '12px', overflow: 'hidden', border: isDark ? '1px solid #233052' : '1px solid #e2e8f0' }}>
                                            {/* Card Header with Rutex corporate border */}
                                            <div className="p-3 border-bottom d-flex justify-content-between align-items-start" style={{ borderColor: cardBorder }}>
                                                <div>
                                                    <div className="fw-bold fs-6 mb-1 text-truncate" style={{ color: textColor, maxWidth: '240px' }} title={cliente?.nombre_comercial || cliente?.razon_social}>
                                                        {cliente?.nombre_comercial || cliente?.razon_social || t('unknown_client', 'Punto de Venta')}
                                                    </div>
                                                    <div className="d-flex align-items-center gap-2 small" style={{ color: textMutedColor }}>
                                                        <span><i className="mdi mdi-card-account-details-outline me-1"></i>{cliente?.rif_cedula || 'S/R'}</span>
                                                        {cliente?.zone?.name && (
                                                            <span>• <i className="mdi mdi-map-marker-outline me-1"></i>{cliente.zone.name}</span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div>
                                                    {renderPopBadge(pop)}
                                                </div>
                                            </div>

                                            {/* Photos Container (Before vs After) */}
                                            <div className="p-2" style={{ backgroundColor: subCardBg }}>
                                                <Row className="g-2">
                                                    {/* Photo Before */}
                                                    <Col xs={6}>
                                                        <div 
                                                            className="position-relative rounded overflow-hidden text-center border" 
                                                            style={{ height: '140px', backgroundColor: isDark ? '#0b1021' : '#f1f5f9', cursor: item.foto_antes_url ? 'pointer' : 'default', borderColor: cardBorder }}
                                                            onClick={() => item.foto_antes_url && setZoomImage({ url: item.foto_antes_url, title: `Antes - ${cliente?.nombre_comercial || ''}` })}
                                                        >
                                                            {item.foto_antes_url ? (
                                                                <>
                                                                    <img 
                                                                        src={item.foto_antes_url} 
                                                                        alt="Antes" 
                                                                        className="w-100 h-100" 
                                                                        style={{ objectFit: 'cover' }} 
                                                                    />
                                                                    <span className="position-absolute top-0 start-0 m-1 badge bg-dark bg-opacity-75 text-white" style={{ fontSize: '0.65rem' }}>
                                                                        <i className="mdi mdi-clock-start me-1"></i>{t('photo_before', 'ANTES')}
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted p-2">
                                                                    <i className="mdi mdi-camera-off-outline fs-4 mb-1"></i>
                                                                    <span style={{ fontSize: '0.7rem' }}>{t('no_photo_before', 'Sin foto')}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </Col>

                                                    {/* Photo After */}
                                                    <Col xs={6}>
                                                        <div 
                                                            className="position-relative rounded overflow-hidden text-center border" 
                                                            style={{ height: '140px', backgroundColor: isDark ? '#0b1021' : '#f1f5f9', cursor: item.foto_despues_url ? 'pointer' : 'default', borderColor: cardBorder }}
                                                            onClick={() => item.foto_despues_url && setZoomImage({ url: item.foto_despues_url, title: `${t('photo_after', 'Después')} - ${cliente?.nombre_comercial || ''}` })}
                                                        >
                                                            {item.foto_despues_url ? (
                                                                <>
                                                                    <img 
                                                                        src={item.foto_despues_url} 
                                                                        alt="Después" 
                                                                        className="w-100 h-100" 
                                                                        style={{ objectFit: 'cover' }} 
                                                                    />
                                                                    <span className="position-absolute top-0 start-0 m-1 badge bg-success text-white" style={{ fontSize: '0.65rem' }}>
                                                                        <i className="mdi mdi-check me-1"></i>{t('photo_after', 'DESPUÉS')}
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted p-2">
                                                                    <i className="mdi mdi-camera-off-outline fs-4 mb-1"></i>
                                                                    <span style={{ fontSize: '0.7rem' }}>{t('no_photo_after', 'Sin foto')}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>

                                            {/* Card Body Metrics */}
                                            <Card.Body className="p-3">
                                                <div className="mb-2 d-flex justify-content-between align-items-center small">
                                                    <span className="text-muted d-flex align-items-center gap-1">
                                                        <i className="mdi mdi-account-tie text-primary"></i>
                                                        <strong>{vendedor?.nombre_completo || t('filter_merchandiser', 'Mercaderista')}</strong>
                                                    </span>
                                                    <span className="text-muted">
                                                        <i className="mdi mdi-calendar-blank-outline me-1"></i>
                                                        {formatVisitDate(visit?.fecha_ruta || item.created_at)}
                                                    </span>
                                                </div>

                                                {/* Highlights Chips with Margen & Hotspots */}
                                                <div className="p-2 rounded mb-3 border" style={{ backgroundColor: subCardBg, borderColor: cardBorder, fontSize: '0.8rem' }}>
                                                    <div className="d-flex justify-content-between mb-1">
                                                        <span className="text-muted">{t('label_initial_inventory', 'Stock tienda:')}</span>
                                                        <span className="fw-semibold" style={{ color: textColor }}>
                                                            {inventario.length > 0 ? `${inventario.length} ${t('products', 'productos')}` : t('not_registered', 'No registrado')}
                                                        </span>
                                                    </div>
                                                    <div className="d-flex justify-content-between mb-1">
                                                        <span className="text-muted">{t('label_out_of_stock', 'Quiebres en Anaquel:')}</span>
                                                        {quiebres.length > 0 ? (
                                                            <span className="fw-bold text-danger">
                                                                <i className="mdi mdi-alert-circle me-1"></i>{quiebres.length} {t('out_of_stock_tag', 'agotados')}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">{t('zero_out_of_stock', '0 quiebres')}</span>
                                                        )}
                                                    </div>

                                                    {/* Margen Percibido */}
                                                    <div className="d-flex justify-content-between align-items-center mb-1 pt-1 border-top" style={{ borderColor: cardBorder }}>
                                                        <span className="text-muted">{t('label_margin', 'Margen Percibido:')}</span>
                                                        <div>{renderMarginBadge(margen)}</div>
                                                    </div>

                                                    {/* Puntos Calientes */}
                                                    <div className="d-flex justify-content-between align-items-start pt-1 border-top" style={{ borderColor: cardBorder }}>
                                                        <span className="text-muted text-nowrap me-2">{t('label_hotspots', 'Ubicación:')}</span>
                                                        <div className="text-end">{renderHotspotsBadges(puntos)}</div>
                                                    </div>
                                                </div>

                                                <Button 
                                                    variant="outline-primary" 
                                                    size="sm" 
                                                    className="w-100 d-flex align-items-center justify-content-center gap-2 py-2 fw-semibold"
                                                    style={{ borderRadius: '6px' }}
                                                    onClick={() => {
                                                        setSelectedReport(item);
                                                        setShowDetailModal(true);
                                                    }}
                                                >
                                                    <i className="mdi mdi-eye-outline"></i>
                                                    {t('view_full_audit', 'Ver Auditoría Completa')}
                                                </Button>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    )}
                </Tab>

                {/* TAB 2: TABLA CONSOLIDADA */}
                <Tab 
                    eventKey="table" 
                    title={
                        <span className="d-flex align-items-center gap-1">
                            <i className="mdi mdi-table"></i>
                            <span>{t('view_table', 'Tabla Consolidada')}</span>
                        </span>
                    }
                >
                    {loading ? (
                        <LoadingState message={t('loading_reports', 'Cargando tabla de auditorías...')} />
                    ) : reports.length === 0 ? (
                        <Card className="text-center p-5 shadow-sm border-0" style={{ backgroundColor: cardBg, borderRadius: '12px' }}>
                            <Card.Body>
                                <i className="mdi mdi-file-search-outline fs-1 text-muted mb-2"></i>
                                <h5 className="fw-bold" style={{ color: textColor }}>{t('no_reports_found', 'No se encontraron auditorías')}</h5>
                            </Card.Body>
                        </Card>
                    ) : (
                        <Card className="shadow-sm border-0" style={{ backgroundColor: cardBg, borderRadius: '12px', overflow: 'hidden' }}>
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle" style={{ color: textColor }}>
                                    {/* Rutex Corporate Blue Table Header */}
                                    <thead style={{ backgroundColor: BRAND_BLUE, color: '#ffffff' }}>
                                        <tr className="text-uppercase small" style={{ letterSpacing: '0.5px', fontSize: '0.78rem' }}>
                                            <th className="py-3 px-3 border-0 text-white" style={{ borderTopLeftRadius: '8px' }}>{t('th_datetime', 'Fecha / Hora')}</th>
                                            <th className="py-3 border-0 text-white">{t('th_pdv', 'Punto de Venta')}</th>
                                            <th className="py-3 border-0 text-white">{t('th_zone', 'Zona')}</th>
                                            <th className="py-3 border-0 text-white">{t('th_merchandiser', 'Mercaderista')}</th>
                                            <th className="py-3 border-0 text-white">{t('col_stock', 'Existencia')}</th>
                                            <th className="py-3 border-0 text-white">{t('col_pop', 'Material POP')}</th>
                                            <th className="py-3 border-0 text-white">{t('col_stockouts', 'Quiebres')}</th>
                                            <th className="py-3 border-0 text-white">{t('col_margin', 'Margen')}</th>
                                            <th className="py-3 border-0 text-white">{t('col_hotspots', 'Puntos Calientes')}</th>
                                            <th className="py-3 border-0 text-white">{t('col_photos', 'Fotos')}</th>
                                            <th className="py-3 px-3 border-0 text-white text-end" style={{ borderTopRightRadius: '8px' }}>{t('col_actions', 'Acciones')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reports.map((item) => {
                                            const cliente = item.visit?.cliente;
                                            const vendedor = item.vendedor;
                                            const visit = item.visit;
                                            const pop = item.material_pop;
                                            const inventario = Array.isArray(item.inventario_pdv) ? item.inventario_pdv : [];
                                            const quiebres = Array.isArray(item.quiebres_stock) ? item.quiebres_stock : [];
                                            const hasPhotos = Boolean(item.foto_antes_url || item.foto_despues_url);

                                            return (
                                                <tr key={item.id} style={{ borderBottom: `1px solid ${cardBorder}` }}>
                                                    <td className="px-3">
                                                        <div className="fw-semibold">
                                                            {formatVisitDate(visit?.fecha_ruta || item.created_at)}
                                                        </div>
                                                        <span className="small text-muted">{visit?.check_in_time ? formatVisitTime(visit.check_in_time) : '-'}</span>
                                                    </td>
                                                    <td>
                                                        <div className="fw-bold" style={{ color: textColor }}>
                                                            {cliente?.nombre_comercial || cliente?.razon_social || t('unknown_client', 'PDV')}
                                                        </div>
                                                        <span className="small text-muted">{cliente?.rif_cedula}</span>
                                                    </td>
                                                    <td>
                                                        <Badge bg="info" className="bg-opacity-10 text-info fw-normal">
                                                            {cliente?.zone?.name || t('unassigned', 'Sin Zona')}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <div className="small fw-semibold">{vendedor?.nombre_completo || '-'}</div>
                                                    </td>
                                                    <td>
                                                        <span className="small">{inventario.length} refs</span>
                                                    </td>
                                                    <td>
                                                        {renderPopBadge(pop)}
                                                    </td>
                                                    <td>
                                                        {quiebres.length > 0 ? (
                                                            <span 
                                                                className="badge rounded-pill fw-bold"
                                                                style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)', color: '#dc2626', border: '1px solid rgba(239, 68, 68, 0.3)' }}
                                                            >
                                                                {quiebres.length} agotados
                                                            </span>
                                                        ) : (
                                                            <span className="small text-muted">0</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {renderMarginBadge(item.margen_percibido)}
                                                    </td>
                                                    <td>
                                                        {renderHotspotsBadges(item.puntos_calientes)}
                                                    </td>
                                                    <td>
                                                        {hasPhotos ? (
                                                            <Badge bg="primary" className="bg-opacity-10 text-primary">
                                                                <i className="mdi mdi-camera me-1"></i>{t('with_photos', 'Con Fotos')}
                                                            </Badge>
                                                        ) : (
                                                            <span className="small text-muted">{t('without_photos', 'Sin fotos')}</span>
                                                        )}
                                                    </td>
                                                    <td className="text-end px-3">
                                                        <Button 
                                                            variant="outline-primary" 
                                                            size="sm"
                                                            style={{ borderRadius: '6px', fontSize: '0.78rem', padding: '4px 10px' }}
                                                            onClick={() => {
                                                                setSelectedReport(item);
                                                                setShowDetailModal(true);
                                                            }}
                                                        >
                                                            <i className="mdi mdi-eye-outline me-1"></i>{t('btn_detail', 'Detalle')}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        </Card>
                    )}
                </Tab>

                {/* TAB 3: INTELIGENCIA DE MERCADO (ANALYTICS) */}
                <Tab 
                    eventKey="analytics" 
                    title={
                        <span className="d-flex align-items-center gap-1">
                            <i className="mdi mdi-chart-box-outline"></i>
                            <span>{t('view_analytics', 'Inteligencia de Mercado')}</span>
                            <Badge bg="info" className="ms-1 text-white" style={{ fontSize: '0.68rem', backgroundColor: '#0284c7' }}>
                                KPIs
                            </Badge>
                        </span>
                    }
                >
                    {loadingAnalytics ? (
                        <LoadingState message={t('loading_analytics', 'Calculando analítica de mercado y visibilidad...')} />
                    ) : !analyticsData ? (
                        <Card className="text-center p-5 shadow-sm border-0" style={{ backgroundColor: cardBg, borderRadius: '12px' }}>
                            <Card.Body>
                                <i className="mdi mdi-chart-box-outline fs-1 text-muted mb-2"></i>
                                <h5 style={{ color: textColor }}>{t('no_analytics_data', 'No hay datos analíticos para los filtros seleccionados')}</h5>
                            </Card.Body>
                        </Card>
                    ) : (
                        <div>
                            {/* Executive Banners Styled with Rutex Brand Palettes */}
                            <Row className="g-3 mb-4">
                                <Col xs={12} md={4}>
                                    <Card 
                                        className="p-3 shadow-sm border-0 text-white" 
                                        style={{ 
                                            background: `linear-gradient(135deg, ${BRAND_NAVY} 0%, ${BRAND_BLUE} 100%)`, 
                                            borderRadius: '12px' 
                                        }}
                                    >
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <span className="small text-white-50 text-uppercase fw-semibold" style={{ letterSpacing: '0.5px' }}>
                                                    {t('analytics_satisfaction', 'Margen Satisfactorio')}
                                                </span>
                                                <h2 className="fw-bold mb-0 mt-1">{analyticsData.kpis?.margen_satisfactorio_porcentaje ?? 0}%</h2>
                                                <span className="small text-white-50">{t('margin_positive_hint', 'PDVs con margen Alto o En el rango')}</span>
                                            </div>
                                            <div 
                                                className="rounded-circle d-flex align-items-center justify-content-center shadow-sm flex-shrink-0"
                                                style={{ 
                                                    width: '54px', 
                                                    height: '54px', 
                                                    minWidth: '54px',
                                                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.18)' : '#ffffff',
                                                    boxShadow: isDark ? 'none' : '0 4px 10px rgba(0,0,0,0.12)'
                                                }}
                                            >
                                                <i 
                                                    className="mdi mdi-scale-balance" 
                                                    style={{ 
                                                        fontSize: '1.75rem',
                                                        color: isDark ? '#ffffff' : BRAND_BLUE 
                                                    }}
                                                ></i>
                                            </div>
                                        </div>
                                    </Card>
                                </Col>

                                <Col xs={12} md={4}>
                                    <Card 
                                        className="p-3 shadow-sm border-0 text-white" 
                                        style={{ 
                                            background: 'linear-gradient(135deg, #065f46 0%, #059669 100%)', 
                                            borderRadius: '12px' 
                                        }}
                                    >
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <span className="small text-white-50 text-uppercase fw-semibold" style={{ letterSpacing: '0.5px' }}>
                                                    {t('analytics_hotspots', 'Puntos Calientes')}
                                                </span>
                                                <h2 className="fw-bold mb-0 mt-1">{analyticsData.kpis?.penetracion_hotspots_porcentaje ?? 0}%</h2>
                                                <span className="small text-white-50">{t('hotspots_covered_hint', 'PDVs con presencia en zonas estratégicas')}</span>
                                            </div>
                                            <div 
                                                className="rounded-circle d-flex align-items-center justify-content-center shadow-sm flex-shrink-0"
                                                style={{ 
                                                    width: '54px', 
                                                    height: '54px', 
                                                    minWidth: '54px',
                                                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.18)' : '#ffffff',
                                                    boxShadow: isDark ? 'none' : '0 4px 10px rgba(0,0,0,0.12)'
                                                }}
                                            >
                                                <i 
                                                    className="mdi mdi-fire" 
                                                    style={{ 
                                                        fontSize: '1.75rem',
                                                        color: isDark ? '#ffffff' : '#059669' 
                                                    }}
                                                ></i>
                                            </div>
                                        </div>
                                    </Card>
                                </Col>

                                <Col xs={12} md={4}>
                                    <Card 
                                        className="p-3 shadow-sm border-0 text-white" 
                                        style={{ 
                                            background: 'linear-gradient(135deg, #991b1b 0%, #ef4444 100%)', 
                                            borderRadius: '12px' 
                                        }}
                                    >
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <span className="small text-white-50 text-uppercase fw-semibold" style={{ letterSpacing: '0.5px' }}>
                                                    {t('analytics_benchmark', 'Precios Competencia')}
                                                </span>
                                                <h2 className="fw-bold mb-0 mt-1">{analyticsData.kpis?.competitor_records_count ?? (analyticsData.kpis?.total_registros_competencia ?? 0)}</h2>
                                                <span className="small text-white-50">{t('competitor_records_hint', 'Registros relevados')}</span>
                                            </div>
                                            <div 
                                                className="rounded-circle d-flex align-items-center justify-content-center shadow-sm flex-shrink-0"
                                                style={{ 
                                                    width: '54px', 
                                                    height: '54px', 
                                                    minWidth: '54px',
                                                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.18)' : '#ffffff',
                                                    boxShadow: isDark ? 'none' : '0 4px 10px rgba(0,0,0,0.12)'
                                                }}
                                            >
                                                <i 
                                                    className="mdi mdi-tag-multiple" 
                                                    style={{ 
                                                        fontSize: '1.75rem',
                                                        color: isDark ? '#ffffff' : '#dc2626' 
                                                    }}
                                                ></i>
                                            </div>
                                        </div>
                                    </Card>
                                </Col>
                            </Row>

                            {/* ACTIONABLE TRIAGE / ALERT BAR */}
                            <div className="p-3 mb-4 rounded-3 border shadow-sm" style={{ backgroundColor: subCardBg, borderColor: cardBorder }}>
                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="mdi mdi-bell-ring-outline text-warning fs-5"></i>
                                        <span className="fw-bold small" style={{ color: textColor }}>
                                            {t('triage_title', 'Alertas de Atención Inmediata')}
                                        </span>
                                    </div>
                                    <span className="small text-muted">{t('triage_hint', 'Haz clic en cualquier alerta para auditar los PDVs afectados')}</span>
                                </div>
                                <Row className="g-3">
                                    {/* Alerta 1: Exhibidores Dañados */}
                                    <Col xs={12} md={4}>
                                        <div 
                                            className="p-3 rounded border d-flex align-items-center justify-content-between transition-all"
                                            style={{ 
                                                backgroundColor: (analyticsData.pop_stats?.exhibidor_danado > 0) ? (isDark ? 'rgba(239, 68, 68, 0.15)' : '#fef2f2') : cardBg,
                                                borderColor: (analyticsData.pop_stats?.exhibidor_danado > 0) ? '#ef4444' : cardBorder,
                                                cursor: (analyticsData.pop_stats?.exhibidor_danado > 0) ? 'pointer' : 'default'
                                            }}
                                            onClick={() => {
                                                if (analyticsData.pop_stats?.exhibidor_danado > 0) {
                                                    handleOpenDrillDown(t('drilldown_damaged_title', 'Comercios con Exhibidores Dañados'), analyticsData.pop_stats?.detalles_danados || [], { badgeVariant: 'danger', type: 'pop' });
                                                }
                                            }}
                                            title={analyticsData.pop_stats?.exhibidor_danado > 0 ? "Clic para ver los comercios con exhibidores dañados" : ""}
                                        >
                                            <div className="d-flex align-items-center gap-3">
                                                <div 
                                                    className="rounded-circle d-flex align-items-center justify-content-center"
                                                    style={{ width: '40px', height: '40px', backgroundColor: analyticsData.pop_stats?.exhibidor_danado > 0 ? '#fee2e2' : '#dcfce7', color: analyticsData.pop_stats?.exhibidor_danado > 0 ? '#dc2626' : '#16a34a' }}
                                                >
                                                    <i className={`mdi ${analyticsData.pop_stats?.exhibidor_danado > 0 ? 'mdi-alert-circle' : 'mdi-check-circle'} fs-5`}></i>
                                                </div>
                                                <div>
                                                    <div className="small fw-bold" style={{ color: textColor }}>{t('triage_damaged_displays', 'Exhibidores Dañados')}</div>
                                                    <div className="small text-muted">{analyticsData.pop_stats?.exhibidor_danado ?? 0} tiendas afectadas</div>
                                                </div>
                                            </div>
                                            <Badge bg={analyticsData.pop_stats?.exhibidor_danado > 0 ? 'danger' : 'success'} className="rounded-pill px-2 py-1">
                                                {analyticsData.pop_stats?.exhibidor_danado > 0 ? t('triage_badge_action', 'Acción Requerida') : t('triage_badge_good', 'Óptimo')}
                                            </Badge>
                                        </div>
                                    </Col>

                                    {/* Alerta 2: Margen Bajo */}
                                    <Col xs={12} md={4}>
                                        <div 
                                            className="p-3 rounded border d-flex align-items-center justify-content-between transition-all"
                                            style={{ 
                                                backgroundColor: (analyticsData.margen_distribucion?.find(m => m.name === 'Bajo')?.cantidad > 0) ? (isDark ? 'rgba(239, 68, 68, 0.15)' : '#fef2f2') : cardBg,
                                                borderColor: (analyticsData.margen_distribucion?.find(m => m.name === 'Bajo')?.cantidad > 0) ? '#ef4444' : cardBorder,
                                                cursor: (analyticsData.margen_distribucion?.find(m => m.name === 'Bajo')?.cantidad > 0) ? 'pointer' : 'default'
                                            }}
                                            onClick={() => {
                                                const lowMarginItem = analyticsData.margen_distribucion?.find(m => m.name === 'Bajo');
                                                if (lowMarginItem?.cantidad > 0) {
                                                    handleOpenDrillDown(t('drilldown_low_margin_title', 'Comercios con Margen Percibido Bajo'), lowMarginItem.comercios || [], { badgeVariant: 'danger', type: 'margin' });
                                                }
                                            }}
                                            title={analyticsData.margen_distribucion?.find(m => m.name === 'Bajo')?.cantidad > 0 ? "Clic para ver comercios en riesgo comercial" : ""}
                                        >
                                            <div className="d-flex align-items-center gap-3">
                                                <div 
                                                    className="rounded-circle d-flex align-items-center justify-content-center"
                                                    style={{ width: '40px', height: '40px', backgroundColor: (analyticsData.margen_distribucion?.find(m => m.name === 'Bajo')?.cantidad > 0) ? '#fee2e2' : '#dcfce7', color: (analyticsData.margen_distribucion?.find(m => m.name === 'Bajo')?.cantidad > 0) ? '#dc2626' : '#16a34a' }}
                                                >
                                                    <i className={`mdi ${analyticsData.margen_distribucion?.find(m => m.name === 'Bajo')?.cantidad > 0 ? 'mdi-trending-down' : 'mdi-trending-up'} fs-5`}></i>
                                                </div>
                                                <div>
                                                    <div className="small fw-bold" style={{ color: textColor }}>{t('triage_low_margin', 'PDVs con Margen Bajo')}</div>
                                                    <div className="small text-muted">{analyticsData.margen_distribucion?.find(m => m.name === 'Bajo')?.cantidad ?? 0} comercios en riesgo</div>
                                                </div>
                                            </div>
                                            <Badge bg={(analyticsData.margen_distribucion?.find(m => m.name === 'Bajo')?.cantidad > 0) ? 'danger' : 'success'} className="rounded-pill px-2 py-1">
                                                {(analyticsData.margen_distribucion?.find(m => m.name === 'Bajo')?.cantidad > 0) ? t('triage_badge_action', 'Acción Requerida') : t('triage_badge_good', 'Óptimo')}
                                            </Badge>
                                        </div>
                                    </Col>

                                    {/* Alerta 3: Quiebres de Stock */}
                                    <Col xs={12} md={4}>
                                        <div 
                                            className="p-3 rounded border d-flex align-items-center justify-content-between transition-all"
                                            style={{ 
                                                backgroundColor: (analyticsData.kpis?.total_quiebres > 0) ? (isDark ? 'rgba(245, 158, 11, 0.15)' : '#fffbeb') : cardBg,
                                                borderColor: (analyticsData.kpis?.total_quiebres > 0) ? '#f59e0b' : cardBorder,
                                                cursor: (analyticsData.top_quiebres?.length > 0) ? 'pointer' : 'default'
                                            }}
                                            onClick={() => {
                                                if (analyticsData.top_quiebres?.length > 0) {
                                                    const top1 = analyticsData.top_quiebres[0];
                                                    handleOpenDrillDown(`${t('drilldown_stockout_for', 'Comercios con Quiebre en: ')} ${top1.nombre}`, top1.clientes_afectados || [], { badgeVariant: 'danger', type: 'stockout' });
                                                }
                                            }}
                                            title={analyticsData.top_quiebres?.length > 0 ? "Clic para ver PDVs con mayor agotamiento" : ""}
                                        >
                                            <div className="d-flex align-items-center gap-3">
                                                <div 
                                                    className="rounded-circle d-flex align-items-center justify-content-center"
                                                    style={{ width: '40px', height: '40px', backgroundColor: analyticsData.kpis?.total_quiebres > 0 ? '#fef3c7' : '#dcfce7', color: analyticsData.kpis?.total_quiebres > 0 ? '#b45309' : '#16a34a' }}
                                                >
                                                    <i className={`mdi ${analyticsData.kpis?.total_quiebres > 0 ? 'mdi-alert-octagon-outline' : 'mdi-check-circle'} fs-5`}></i>
                                                </div>
                                                <div>
                                                    <div className="small fw-bold" style={{ color: textColor }}>{t('triage_critical_stockouts', 'Quiebres Críticos')}</div>
                                                    <div className="small text-muted">{analyticsData.kpis?.total_quiebres ?? 0} faltantes ({analyticsData.top_quiebres?.length ?? 0} SKUs)</div>
                                                </div>
                                            </div>
                                            <Badge bg={analyticsData.kpis?.total_quiebres > 0 ? 'warning' : 'success'} className={`rounded-pill px-2 py-1 ${analyticsData.kpis?.total_quiebres > 0 ? 'text-dark' : ''}`}>
                                                {analyticsData.kpis?.total_quiebres > 0 ? t('triage_badge_action', 'Acción Requerida') : t('triage_badge_good', 'Óptimo')}
                                            </Badge>
                                        </div>
                                    </Col>
                                </Row>
                            </div>

                            {/* EJE 1: TRADE MARKETING & VISIBILIDAD EN GÓNDOLA */}
                            <div className="mb-4">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <div>
                                        <h5 className="fw-bold mb-0 d-flex align-items-center gap-2" style={{ color: textColor }}>
                                            <i className="mdi mdi-store-search text-primary"></i>
                                            {t('axis_trade_marketing', '1. Trade Marketing y Visibilidad en Tienda')}
                                        </h5>
                                        <span className="small text-muted">{t('axis_trade_marketing_subtitle', 'Estado de material POP y presencia en zonas estratégicas de alto tráfico')}</span>
                                    </div>
                                </div>

                                <Row className="g-4">
                                    {/* Puntos Calientes */}
                                    <Col xs={12} lg={6}>
                                        <Card className="h-100 shadow-sm border-0 p-3" style={{ backgroundColor: cardBg, borderRadius: '12px', border: isDark ? '1px solid #233052' : '1px solid #e2e8f0' }}>
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <div>
                                                    <h6 className="fw-bold mb-0" style={{ color: textColor }}>
                                                        <i className="mdi mdi-fire text-warning me-2"></i>
                                                        {t('chart_hotspots_title', 'Penetración en Puntos Calientes')}
                                                    </h6>
                                                    <span className="small text-muted">{t('chart_hotspots_hint', 'Zonas estratégicas de alta visibilidad en PDVs')}</span>
                                                </div>
                                                <span 
                                                    className="badge rounded-pill d-inline-flex align-items-center gap-1"
                                                    style={{ 
                                                        backgroundColor: isDark ? 'rgba(245, 158, 11, 0.2)' : 'rgba(245, 158, 11, 0.1)', 
                                                        color: isDark ? '#fde047' : '#b45309', 
                                                        border: isDark ? '1px solid rgba(253, 224, 71, 0.4)' : '1px solid rgba(245, 158, 11, 0.3)', 
                                                        fontSize: '0.74rem',
                                                        padding: '4px 9px',
                                                        fontWeight: '600'
                                                    }}
                                                >
                                                    <i className="mdi mdi-fire"></i>{analyticsData.puntos_calientes?.penetracion_global_porcentaje ?? 0}% global
                                                </span>
                                            </div>

                                            <Row className="g-3">
                                                {(analyticsData.puntos_calientes?.distribucion_tipos || []).map((spot) => (
                                                    <Col xs={12} sm={6} key={spot.clave}>
                                                        <div 
                                                            className="p-3 rounded border h-100 d-flex flex-column justify-content-between"
                                                            style={{ backgroundColor: subCardBg, borderColor: cardBorder }}
                                                        >
                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                <span className="fw-semibold small" style={{ color: textColor }}>{spot.nombre}</span>
                                                                <span 
                                                                    className="badge rounded-pill fw-bold"
                                                                    style={{ backgroundColor: isDark ? 'rgba(24, 62, 235, 0.2)' : 'rgba(24, 62, 235, 0.12)', color: isDark ? '#93c5fd' : BRAND_BLUE, border: isDark ? '1px solid rgba(147, 197, 253, 0.4)' : '1px solid rgba(24, 62, 235, 0.3)' }}
                                                                >
                                                                    {spot.porcentaje}%
                                                                </span>
                                                            </div>
                                                            <div>
                                                                <ProgressBar 
                                                                    now={spot.porcentaje} 
                                                                    variant={spot.clave === 'nivel_ojos' ? 'info' : (spot.clave === 'cabecera_gondola' ? 'primary' : 'warning')} 
                                                                    style={{ height: '8px', borderRadius: '4px' }} 
                                                                />
                                                                <div className="d-flex justify-content-between align-items-center mt-2 small">
                                                                    <span className="text-muted">{spot.cantidad} auditorías</span>
                                                                    <Button 
                                                                        size="sm" 
                                                                        variant="link" 
                                                                        className="p-0 text-decoration-none small fw-bold" 
                                                                        style={{ color: BRAND_BLUE }}
                                                                        onClick={() => handleOpenDrillDown(`${t('drilldown_hotspot_for', 'Comercios con Presencia en: ')} ${spot.nombre}`, spot.comercios || [], { badgeVariant: 'primary', type: 'hotspot' })}
                                                                    >
                                                                        <i className="mdi mdi-store-search-outline me-1"></i>
                                                                        {t('view_affected_stores', 'Ver Tiendas')}
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                ))}
                                            </Row>

                                            {/* Hotspot Penetration by Zone */}
                                            {Array.isArray(analyticsData.puntos_calientes?.por_zona) && analyticsData.puntos_calientes.por_zona.length > 0 && (
                                                <div className="mt-4 pt-3 border-top" style={{ borderColor: cardBorder }}>
                                                    <h6 className="fw-bold small mb-2 text-muted">{t('hotspots_by_zone', 'Penetración por Zona Comercial:')}</h6>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {analyticsData.puntos_calientes.por_zona.map((pz, idx) => (
                                                            <div 
                                                                key={idx} 
                                                                className="px-3 py-2 rounded border small d-flex align-items-center gap-2"
                                                                style={{ backgroundColor: subCardBg, borderColor: cardBorder }}
                                                            >
                                                                <i className="mdi mdi-map-marker-outline text-info"></i>
                                                                <strong style={{ color: textColor }}>{pz.zona}</strong>
                                                                <span 
                                                                    className="badge rounded-pill"
                                                                    style={{ backgroundColor: isDark ? 'rgba(16, 185, 129, 0.22)' : 'rgba(5, 150, 105, 0.12)', color: isDark ? '#6ee7b7' : '#059669', border: isDark ? '1px solid rgba(110, 231, 183, 0.4)' : '1px solid rgba(5, 150, 105, 0.3)', fontWeight: '600' }}
                                                                >
                                                                    {pz.penetracion_porcentaje}% activo
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </Card>
                                    </Col>

                                    {/* Material POP y Exhibidores */}
                                    <Col xs={12} lg={6}>
                                        <Card className="h-100 shadow-sm border-0 p-3" style={{ backgroundColor: cardBg, borderRadius: '12px', border: isDark ? '1px solid #233052' : '1px solid #e2e8f0' }}>
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <div>
                                                    <h6 className="fw-bold mb-0" style={{ color: textColor }}>
                                                        <i className="mdi mdi-store-check text-success me-2"></i>
                                                        {t('chart_pop_title', 'Material POP y Exhibición')}
                                                    </h6>
                                                    <span className="small text-muted">{t('chart_pop_hint', 'Estado operativo de exhibición')}</span>
                                                </div>
                                                <span 
                                                    className="badge rounded-pill d-inline-flex align-items-center gap-1"
                                                    style={{ 
                                                        backgroundColor: isDark ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.1)', 
                                                        color: isDark ? '#6ee7b7' : '#059669', 
                                                        border: isDark ? '1px solid rgba(110, 231, 183, 0.4)' : '1px solid rgba(16, 185, 129, 0.3)', 
                                                        fontSize: '0.74rem',
                                                        padding: '4px 9px',
                                                        fontWeight: '600'
                                                    }}
                                                >
                                                    <i className="mdi mdi-store-check-outline"></i>{t('th_pop', 'Material POP')}
                                                </span>
                                            </div>

                                            <div className="d-flex flex-column gap-3">
                                                {/* Exhibidor Principal */}
                                                <div className="p-3 rounded border" style={{ backgroundColor: subCardBg, borderColor: cardBorder }}>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <i className="mdi mdi-store-24-hour fs-5 text-primary"></i>
                                                            <strong style={{ color: textColor }}>{t('pop_main_display', 'Exhibidor Principal')}</strong>
                                                        </div>
                                                        <span className="small text-muted">{analyticsData.pop_stats?.exhibidor_buen_estado ?? 0} operativos</span>
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <div 
                                                            className="badge rounded-pill flex-fill py-2 text-center shadow-sm"
                                                            style={{ backgroundColor: isDark ? 'rgba(16, 185, 129, 0.22)' : 'rgba(5, 150, 105, 0.15)', color: isDark ? '#6ee7b7' : '#059669', border: isDark ? '1px solid rgba(110, 231, 183, 0.4)' : '1px solid rgba(5, 150, 105, 0.3)', fontWeight: '600', cursor: 'pointer' }}
                                                            onClick={() => handleOpenDrillDown(t('drilldown_good_display_title', 'Comercios con Exhibidor en Buen Estado'), analyticsData.pop_stats?.detalles_buen_estado || [], { badgeVariant: 'success', type: 'pop' })}
                                                            title="Clic para ver tiendas con exhibidor en buen estado"
                                                        >
                                                            <i className="mdi mdi-check-circle-outline me-1"></i>{analyticsData.pop_stats?.exhibidor_buen_estado ?? 0} OK
                                                        </div>
                                                        <div 
                                                            className="badge rounded-pill flex-fill py-2 text-center shadow-sm"
                                                            style={{ backgroundColor: isDark ? 'rgba(239, 68, 68, 0.22)' : 'rgba(239, 68, 68, 0.15)', color: isDark ? '#fca5a5' : '#dc2626', border: isDark ? '1px solid rgba(252, 165, 165, 0.4)' : '1px solid rgba(239, 68, 68, 0.3)', fontWeight: '600', cursor: 'pointer' }}
                                                            onClick={() => handleOpenDrillDown(t('drilldown_damaged_title', 'Comercios con Exhibidores Dañados'), analyticsData.pop_stats?.detalles_danados || [], { badgeVariant: 'danger', type: 'pop' })}
                                                            title="Clic para ver tiendas con exhibidores dañados"
                                                        >
                                                            <i className="mdi mdi-alert-circle-outline me-1"></i>{analyticsData.pop_stats?.exhibidor_danado ?? 0} Dañados
                                                        </div>
                                                        <div 
                                                            className="badge rounded-pill flex-fill py-2 text-center shadow-sm"
                                                            style={{ backgroundColor: isDark ? 'rgba(148, 163, 184, 0.2)' : 'rgba(100, 116, 139, 0.15)', color: isDark ? '#cbd5e1' : '#64748b', border: isDark ? '1px solid rgba(148, 163, 184, 0.4)' : '1px solid rgba(100, 116, 139, 0.3)', fontWeight: '600', cursor: 'pointer' }}
                                                            onClick={() => handleOpenDrillDown(t('drilldown_no_display_title', 'Comercios Sin Exhibidor Principal'), analyticsData.pop_stats?.detalles_sin_exhibidor || [], { badgeVariant: 'secondary', type: 'pop' })}
                                                            title="Clic para ver tiendas sin exhibidor"
                                                        >
                                                            <i className="mdi mdi-close-circle-outline me-1"></i>{analyticsData.pop_stats?.exhibidor_no_tiene ?? 0} Sin exhibidor
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Habladores de Góndola */}
                                                <div className="p-3 rounded border" style={{ backgroundColor: subCardBg, borderColor: cardBorder }}>
                                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <i className="mdi mdi-tag-text-outline fs-5 text-info"></i>
                                                            <strong style={{ color: textColor }}>{t('pop_shelf_talkers', 'Habladores de Góndola')}</strong>
                                                        </div>
                                                        <span 
                                                            className="badge rounded-pill"
                                                            style={{ backgroundColor: isDark ? 'rgba(2, 132, 199, 0.22)' : 'rgba(2, 132, 199, 0.15)', color: isDark ? '#38bdf8' : '#0284c7', border: isDark ? '1px solid rgba(56, 189, 248, 0.4)' : '1px solid rgba(2, 132, 199, 0.3)', fontWeight: '600' }}
                                                        >
                                                            {analyticsData.pop_stats?.habladores_buen_estado ?? 0} activos
                                                        </span>
                                                    </div>
                                                    <span className="small text-muted">{t('pop_talkers_hint', 'Identificadores de precio y mensaje colocados en góndola.')}</span>
                                                </div>

                                                {/* Afiches Promocionales */}
                                                <div className="p-3 rounded border" style={{ backgroundColor: subCardBg, borderColor: cardBorder }}>
                                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <i className="mdi mdi-newspaper-variant-outline fs-5" style={{ color: '#8b5cf6' }}></i>
                                                            <strong style={{ color: textColor }}>{t('pop_posters', 'Afiches Promocionales')}</strong>
                                                        </div>
                                                        <span 
                                                            className="badge rounded-pill"
                                                            style={{ backgroundColor: isDark ? 'rgba(139, 92, 246, 0.22)' : 'rgba(139, 92, 246, 0.15)', color: isDark ? '#c4b5fd' : '#8b5cf6', border: isDark ? '1px solid rgba(196, 181, 253, 0.4)' : '1px solid rgba(139, 92, 246, 0.3)', fontWeight: '600' }}
                                                        >
                                                            {analyticsData.pop_stats?.afiches_buen_estado ?? 0} visibles
                                                        </span>
                                                    </div>
                                                    <span className="small text-muted">{t('pop_posters_hint', 'Cartelería gráfica en puntos estratégicos.')}</span>
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>
                                </Row>
                            </div>

                            {/* EJE 2: COMPETITIVIDAD DE PRECIOS Y MARGEN PERCIBIDO */}
                            <div className="mb-4">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <div>
                                        <h5 className="fw-bold mb-0 d-flex align-items-center gap-2" style={{ color: textColor }}>
                                            <i className="mdi mdi-scale-balance text-success"></i>
                                            {t('axis_pricing_margin', '2. Competitividad de Precios y Margen Percibido')}
                                        </h5>
                                        <span className="small text-muted">{t('axis_pricing_margin_subtitle', 'Evaluación de rentabilidad del cliente cruzada con precios de la competencia')}</span>
                                    </div>
                                </div>

                                <Row className="g-4">
                                    {/* Margen Percibido Donut */}
                                    <Col xs={12} lg={5}>
                                        <Card className="h-100 shadow-sm border-0 p-3" style={{ backgroundColor: cardBg, borderRadius: '12px', border: isDark ? '1px solid #233052' : '1px solid #e2e8f0' }}>
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <div>
                                                    <h6 className="fw-bold mb-0" style={{ color: textColor }}>
                                                        <i className="mdi mdi-chart-donut text-info me-2"></i>
                                                        {t('chart_margin_title', 'Distribución de Margen Percibido')}
                                                    </h6>
                                                    <span className="small text-muted">{t('chart_margin_subtitle', 'Opinión del comercio sobre la rentabilidad')}</span>
                                                </div>
                                                <span 
                                                    className="badge rounded-pill d-inline-flex align-items-center gap-1"
                                                    style={{ 
                                                        backgroundColor: isDark ? 'rgba(2, 132, 199, 0.2)' : 'rgba(2, 132, 199, 0.1)', 
                                                        color: isDark ? '#38bdf8' : '#0284c7', 
                                                        border: isDark ? '1px solid rgba(56, 189, 248, 0.4)' : '1px solid rgba(2, 132, 199, 0.3)', 
                                                        fontSize: '0.74rem',
                                                        padding: '4px 9px',
                                                        fontWeight: '600'
                                                    }}
                                                >
                                                    <i className="mdi mdi-scale-balance"></i>{t('col_margin', 'Margen')}
                                                </span>
                                            </div>

                                            {Array.isArray(analyticsData.margen_distribucion) && (
                                                <div>
                                                    <div style={{ height: '220px', width: '100%' }}>
                                                        <ResponsiveContainer width="100%" height="100%">
                                                            <PieChart>
                                                                <Pie
                                                                    data={analyticsData.margen_distribucion}
                                                                    dataKey="cantidad"
                                                                    nameKey="name"
                                                                    cx="50%"
                                                                    cy="50%"
                                                                    innerRadius={50}
                                                                    outerRadius={80}
                                                                    paddingAngle={4}
                                                                >
                                                                    {analyticsData.margen_distribucion.map((entry, index) => (
                                                                        <Cell key={`cell-${index}`} fill={MARGIN_COLORS[entry.name] || '#94a3b8'} />
                                                                    ))}
                                                                </Pie>
                                                                <Tooltip 
                                                                    formatter={(val, name) => [`${val} PDVs (${analyticsData.margen_distribucion.find(m => m.name === name)?.porcentaje || 0}%)`, name]}
                                                                    contentStyle={{ 
                                                                        backgroundColor: cardBg, 
                                                                        borderColor: cardBorder, 
                                                                        color: textColor, 
                                                                        borderRadius: '8px',
                                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                                                    }}
                                                                />
                                                            </PieChart>
                                                        </ResponsiveContainer>
                                                    </div>

                                                    {/* Margin Callouts Clickable with DrillDown */}
                                                    <div className="d-flex flex-column gap-2 mt-2">
                                                        {analyticsData.margen_distribucion.map((m, idx) => (
                                                            <div 
                                                                key={idx} 
                                                                className="d-flex justify-content-between align-items-center p-2 px-3 rounded border transition-all"
                                                                style={{ backgroundColor: subCardBg, borderColor: cardBorder }}
                                                            >
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <div 
                                                                        style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: MARGIN_COLORS[m.name] || '#94a3b8' }} 
                                                                    />
                                                                    <span className="small fw-bold" style={{ color: textColor }}>{m.name}</span>
                                                                </div>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <span className="small text-muted">{m.cantidad} {t('pdvs', 'PDVs')}</span>
                                                                    <span 
                                                                        className="badge rounded-pill fw-bold"
                                                                        style={{ 
                                                                            backgroundColor: m.name === 'Alto' ? (isDark ? 'rgba(16, 185, 129, 0.22)' : 'rgba(16, 185, 129, 0.12)') : (m.name === 'En el rango' ? (isDark ? 'rgba(2, 132, 199, 0.22)' : 'rgba(2, 132, 199, 0.12)') : (isDark ? 'rgba(239, 68, 68, 0.22)' : 'rgba(239, 68, 68, 0.12)')),
                                                                            color: m.name === 'Alto' ? (isDark ? '#6ee7b7' : '#059669') : (m.name === 'En el rango' ? (isDark ? '#38bdf8' : '#0284c7') : (isDark ? '#fca5a5' : '#dc2626')),
                                                                            border: `1px solid ${m.name === 'Alto' ? (isDark ? 'rgba(110, 231, 183, 0.4)' : 'rgba(16, 185, 129, 0.3)') : (m.name === 'En el rango' ? (isDark ? 'rgba(56, 189, 248, 0.4)' : 'rgba(2, 132, 199, 0.3)') : (isDark ? 'rgba(252, 165, 165, 0.4)' : 'rgba(239, 68, 68, 0.3)'))}`
                                                                        }}
                                                                    >
                                                                        {m.porcentaje}%
                                                                    </span>
                                                                    {m.cantidad > 0 && (
                                                                        <Button 
                                                                            size="sm" 
                                                                            variant="link" 
                                                                            className="p-0 text-decoration-none small fw-bold ms-1"
                                                                            style={{ color: BRAND_BLUE }}
                                                                            onClick={() => handleOpenDrillDown(
                                                                                m.name === 'Alto' ? t('drilldown_high_margin_title', 'Comercios con Margen Percibido Alto') : (m.name === 'En el rango' ? t('drilldown_in_range_margin_title', 'Comercios con Margen en el Rango') : t('drilldown_low_margin_title', 'Comercios con Margen Percibido Bajo')),
                                                                                m.comercios || [],
                                                                                { badgeVariant: m.name === 'Alto' ? 'success' : (m.name === 'En el rango' ? 'info' : 'danger'), type: 'margin' }
                                                                            )}
                                                                        >
                                                                            <i className="mdi mdi-eye-outline me-1"></i>Ver
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </Card>
                                    </Col>

                                    {/* Benchmarking Precios Competencia */}
                                    <Col xs={12} lg={7}>
                                        <Card className="h-100 shadow-sm border-0 p-3" style={{ backgroundColor: cardBg, borderRadius: '12px', border: isDark ? '1px solid #233052' : '1px solid #e2e8f0' }}>
                                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                                                <div>
                                                    <h6 className="fw-bold mb-0" style={{ color: textColor }}>
                                                        <i className="mdi mdi-currency-usd text-warning me-2"></i>
                                                        {t('competitor_benchmark_title', 'Benchmarking de Precios de Competencia')}
                                                    </h6>
                                                    <span className="small text-muted">{t('competitor_benchmark_subtitle', 'Comparativa histórica en dólares relevados en visitas')}</span>
                                                </div>

                                                <div className="d-flex align-items-center gap-2">
                                                    <Form.Control 
                                                        type="text" 
                                                        size="sm"
                                                        placeholder={t('search_competitor_placeholder', 'Filtrar marca o producto...')}
                                                        value={competitorSearch}
                                                        onChange={(e) => setCompetitorSearch(e.target.value)}
                                                        className="customer-filter-control shadow-none"
                                                        style={{ width: '220px' }}
                                                    />
                                                    <Badge bg="primary" className="px-2 py-1">
                                                        {filteredCompetitors.length} reg.
                                                    </Badge>
                                                </div>
                                            </div>

                                            {/* Brand summary chips */}
                                            {Array.isArray(analyticsData.precios_competencia?.resumen_marcas) && analyticsData.precios_competencia.resumen_marcas.length > 0 && (
                                                <div className="d-flex flex-wrap gap-2 mb-3 pb-3 border-bottom" style={{ borderColor: cardBorder }}>
                                                    {analyticsData.precios_competencia.resumen_marcas.slice(0, 5).map((brand, idx) => (
                                                        <div 
                                                            key={idx}
                                                            className="p-1 px-2 rounded border small d-flex align-items-center gap-2"
                                                            style={{ backgroundColor: subCardBg, borderColor: cardBorder, cursor: 'pointer' }}
                                                            onClick={() => setCompetitorSearch(brand.marca)}
                                                            title={`Clic para filtrar por ${brand.marca}`}
                                                        >
                                                            <strong style={{ color: textColor }}>{brand.marca}</strong>
                                                            <span className="text-muted">({brand.registros})</span>
                                                            <span 
                                                                className="badge rounded-pill fw-bold"
                                                                style={{ backgroundColor: 'rgba(245, 158, 11, 0.15)', color: '#b45309', border: '1px solid rgba(245, 158, 11, 0.3)' }}
                                                            >
                                                                ${brand.precio_promedio.toFixed(2)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {filteredCompetitors.length > 0 ? (
                                                <div className="table-responsive rounded border" style={{ borderColor: cardBorder, maxHeight: '280px', overflowY: 'auto' }}>
                                                    <Table hover className="mb-0 align-middle table-sm" style={{ color: textColor }}>
                                                        <thead style={{ backgroundColor: BRAND_BLUE, color: '#ffffff', position: 'sticky', top: 0, zIndex: 1 }}>
                                                            <tr className="small text-uppercase" style={{ letterSpacing: '0.5px' }}>
                                                                <th className="py-2 px-3 text-white border-0">{t('col_date', 'Fecha')}</th>
                                                                <th className="py-2 text-white border-0">{t('col_customer', 'PDV / Cliente')}</th>
                                                                <th className="py-2 text-white border-0">{t('col_rival_brand', 'Marca Rival')}</th>
                                                                <th className="py-2 text-white border-0">{t('col_competitor_product', 'Producto')}</th>
                                                                <th className="py-2 text-end text-white border-0">{t('col_price_usd', 'Precio ($)')}</th>
                                                                <th className="py-2 px-3 text-end text-white border-0">{t('col_actions', 'Acción')}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {filteredCompetitors.map((c, idx) => (
                                                                <tr key={idx} style={{ borderBottom: `1px solid ${cardBorder}` }}>
                                                                    <td className="px-3 small">{c.fecha}</td>
                                                                    <td>
                                                                        <div className="fw-semibold small text-truncate" style={{ maxWidth: '180px' }} title={c.cliente}>
                                                                            {c.cliente}
                                                                        </div>
                                                                    </td>
                                                                    <td className="fw-bold small" style={{ color: '#d97706' }}>{c.marca}</td>
                                                                    <td className="small text-truncate" style={{ maxWidth: '140px' }} title={c.producto}>{c.producto}</td>
                                                                    <td className="text-end fw-bold text-success font-monospace">
                                                                        $ {Number(c.precio_usd).toFixed(2)}
                                                                    </td>
                                                                    <td className="px-3 text-end">
                                                                        <Button 
                                                                            size="sm" 
                                                                            variant="outline-primary" 
                                                                            className="py-0 px-2 border-0"
                                                                            style={{ borderRadius: '6px', fontSize: '0.78rem' }}
                                                                            onClick={() => handleOpenReportById(c.report_id)}
                                                                            title={t('drilldown_view_audit', 'Ver Auditoría Completa')}
                                                                        >
                                                                            <i className="mdi mdi-eye-outline me-1"></i>Ver
                                                                        </Button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            ) : (
                                                <div className="p-4 text-center text-muted">
                                                    <i className="mdi mdi-tag-remove-outline fs-2 mb-2"></i>
                                                    <p className="mb-0">{t('no_competitor_records', 'No hay registros de precios de competencia para la búsqueda actual.')}</p>
                                                </div>
                                            )}
                                        </Card>
                                    </Col>
                                </Row>
                            </div>

                            {/* EJE 3: DISPONIBILIDAD Y FALTANTES EN ANAQUEL */}
                            <div className="mb-4">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <div>
                                        <h5 className="fw-bold mb-0 d-flex align-items-center gap-2" style={{ color: textColor }}>
                                            <i className="mdi mdi-alert-circle text-danger"></i>
                                            {t('axis_stockouts', '3. Disponibilidad y Faltantes en Anaquel')}
                                        </h5>
                                        <span className="small text-muted">{t('axis_stockouts_subtitle', 'Productos con ausencia física de inventario en góndolas auditadas')}</span>
                                    </div>
                                    <span 
                                        className="badge rounded-pill d-inline-flex align-items-center gap-1"
                                        style={{ 
                                            backgroundColor: isDark ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.1)', 
                                            color: isDark ? '#fca5a5' : '#dc2626', 
                                            border: isDark ? '1px solid rgba(252, 165, 165, 0.4)' : '1px solid rgba(239, 68, 68, 0.3)', 
                                            fontSize: '0.74rem',
                                            padding: '4px 9px',
                                            fontWeight: '600'
                                        }}
                                    >
                                        <i className="mdi mdi-alert-octagon-outline"></i>{analyticsData.kpis?.total_quiebres ?? 0} {t('out_of_stock_tag', 'Agotados')}
                                    </span>
                                </div>

                                <Card className="shadow-sm border-0 p-3" style={{ backgroundColor: cardBg, borderRadius: '12px', border: isDark ? '1px solid #233052' : '1px solid #e2e8f0' }}>
                                    {Array.isArray(analyticsData.top_quiebres) && analyticsData.top_quiebres.length > 0 ? (
                                        <Row className="g-4 align-items-center">
                                            {/* BarChart Top Quiebres */}
                                            <Col xs={12} lg={5}>
                                                <div style={{ height: '300px', width: '100%' }}>
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <BarChart 
                                                            data={analyticsData.top_quiebres.slice(0, 6)}
                                                            layout="vertical"
                                                            margin={{ top: 5, right: 25, left: 35, bottom: 5 }}
                                                        >
                                                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#233052' : '#f1f5f9'} />
                                                            <XAxis type="number" stroke={textMutedColor} />
                                                            <YAxis 
                                                                dataKey="sku" 
                                                                type="category" 
                                                                stroke={textMutedColor}
                                                                width={75}
                                                            />
                                                            <Tooltip 
                                                                formatter={(value) => [`${value} reportes`, 'Quiebres']}
                                                                labelFormatter={(label) => `SKU: ${label}`}
                                                                contentStyle={{ 
                                                                    backgroundColor: cardBg, 
                                                                    borderColor: cardBorder, 
                                                                    color: textColor, 
                                                                    borderRadius: '8px',
                                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                                                }}
                                                            />
                                                            <Bar dataKey="quiebres" fill="#ef4444" radius={[0, 6, 6, 0]} />
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </Col>

                                            {/* Impact Table with DrillDown Action */}
                                            <Col xs={12} lg={7}>
                                                <div className="table-responsive rounded border" style={{ borderColor: cardBorder }}>
                                                    <Table hover size="sm" className="mb-0 align-middle" style={{ color: textColor }}>
                                                        <thead style={{ backgroundColor: BRAND_BLUE, color: '#ffffff' }}>
                                                            <tr className="small text-uppercase" style={{ letterSpacing: '0.5px' }}>
                                                                <th className="py-2 px-2 text-white border-0" style={{ borderTopLeftRadius: '6px' }}>SKU</th>
                                                                <th className="py-2 text-white border-0">{t('product', 'Producto')}</th>
                                                                <th className="py-2 text-white border-0">{t('category', 'Categoría')}</th>
                                                                <th className="py-2 text-center text-white border-0">{t('pdvs_affected', 'PDVs')}</th>
                                                                <th className="py-2 text-end text-white border-0">{t('impact', '% Afectación')}</th>
                                                                <th className="py-2 px-2 text-end text-white border-0" style={{ borderTopRightRadius: '6px' }}>{t('col_actions', 'Acción')}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {analyticsData.top_quiebres.slice(0, 6).map((q, idx) => (
                                                                <tr key={idx} style={{ borderBottom: `1px solid ${cardBorder}` }}>
                                                                    <td className="px-2">
                                                                        <Badge bg="dark" className="font-monospace text-light">{q.sku}</Badge>
                                                                    </td>
                                                                    <td className="small fw-semibold text-truncate" style={{ maxWidth: '200px' }} title={q.nombre}>
                                                                        {q.nombre}
                                                                    </td>
                                                                    <td>
                                                                        <Badge bg="secondary" className="bg-opacity-10 text-secondary">{q.categoria}</Badge>
                                                                    </td>
                                                                    <td className="text-center fw-bold text-danger">{q.pdvs_afectados}</td>
                                                                    <td className="text-end fw-bold text-danger">{q.porcentaje_afectacion}%</td>
                                                                    <td className="text-end px-2">
                                                                        <Button 
                                                                            size="sm" 
                                                                            variant="outline-danger" 
                                                                            className="py-1 px-2 border-0"
                                                                            style={{ borderRadius: '6px', fontSize: '0.78rem' }}
                                                                            onClick={() => handleOpenDrillDown(`${t('drilldown_stockout_for', 'Comercios con Quiebre en: ')} ${q.nombre}`, q.clientes_afectados || [], { badgeVariant: 'danger', type: 'stockout' })}
                                                                            title="Ver comercios con este producto agotado"
                                                                        >
                                                                            <i className="mdi mdi-store-search-outline me-1"></i>Ver PDVs
                                                                        </Button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </Col>
                                        </Row>
                                    ) : (
                                        <div className="p-4 text-center text-muted">
                                            <i className="mdi mdi-check-circle-outline fs-1 text-success mb-2"></i>
                                            <p className="mb-0">{t('no_stockouts_recorded', 'No se registraron quiebres de stock en el período seleccionado.')}</p>
                                        </div>
                                    )}
                                </Card>
                            </div>
                        </div>
                    )}
                </Tab>
            </Tabs>

            {/* Pagination Controls (Only in gallery or table mode) */}
            {viewMode !== 'analytics' && pagination.total > pagination.per_page && (
                <div className="d-flex justify-content-between align-items-center mt-4">
                    <span className="small text-muted">
                        {t('showing_reports', 'Mostrando {{count}} de {{total}} auditorías', { count: reports.length, total: pagination.total })}
                    </span>
                    <div className="d-flex gap-2">
                        <Button 
                            variant="outline-secondary" 
                            size="sm" 
                            disabled={pagination.current_page <= 1}
                            onClick={() => fetchReports(pagination.current_page - 1)}
                            style={{ borderRadius: '6px' }}
                        >
                            <i className="mdi mdi-chevron-left me-1"></i>{t('previous', 'Anterior')}
                        </Button>
                        <span className="d-flex align-items-center px-3 small fw-bold" style={{ color: textColor }}>
                            {t('page_x_of_y', 'Pág. {{current}} de {{last}}', { current: pagination.current_page, last: pagination.last_page })}
                        </span>
                        <Button 
                            variant="outline-secondary" 
                            size="sm" 
                            disabled={pagination.current_page >= pagination.last_page}
                            onClick={() => fetchReports(pagination.current_page + 1)}
                            style={{ borderRadius: '6px' }}
                        >
                            {t('next', 'Siguiente')}<i className="mdi mdi-chevron-right ms-1"></i>
                        </Button>
                    </div>
                </div>
            )}

            {/* DRILL-DOWN MODAL (OPERATIONAL DETAILS & PDV TRACEABILITY) */}
            <Modal
                show={drillDownModal.show}
                onHide={() => setDrillDownModal(prev => ({ ...prev, show: false }))}
                size="lg"
                centered
                scrollable
                style={{ zIndex: 1050 }}
            >
                <Modal.Header 
                    closeButton 
                    closeVariant="white"
                    style={{ backgroundColor: BRAND_NAVY, color: '#ffffff', borderBottom: `3px solid ${BRAND_BLUE}` }}
                >
                    <div className="d-flex align-items-center justify-content-between w-100 me-3">
                        <div className="d-flex align-items-center gap-2">
                            <div 
                                className="rounded-circle d-flex align-items-center justify-content-center text-white" 
                                style={{ width: '38px', height: '38px', backgroundColor: BRAND_BLUE }}
                            >
                                <i className="mdi mdi-filter-variant-plus fs-5"></i>
                            </div>
                            <div>
                                <Modal.Title className="fs-6 fw-bold mb-0 text-white">
                                    {drillDownModal.title || t('drilldown_title', 'Detalle Operativo de Comercios')}
                                </Modal.Title>
                                <div className="small text-white-50" style={{ fontSize: '0.78rem' }}>
                                    {drillDownModal.subtitle || t('drilldown_subtitle', 'Trazabilidad directa al reporte individual relevado por el mercaderista')}
                                </div>
                            </div>
                        </div>
                        <Badge 
                            bg={drillDownModal.badgeVariant || 'primary'} 
                            className="px-3 py-2 rounded-pill fw-semibold"
                        >
                            {drillDownModal.badgeText || `${drillDownModal.items?.length || 0} PDVs`}
                        </Badge>
                    </div>
                </Modal.Header>

                <Modal.Body className="p-3" style={{ backgroundColor: cardBg, color: textColor }}>
                    {drillDownModal.items && drillDownModal.items.length > 0 ? (
                        <div className="table-responsive rounded border" style={{ borderColor: cardBorder, maxHeight: '60vh', overflowY: 'auto' }}>
                            <Table hover className="mb-0 align-middle table-sm" style={{ color: textColor }}>
                                <thead style={{ backgroundColor: BRAND_BLUE, color: '#ffffff', position: 'sticky', top: 0, zIndex: 1 }}>
                                    <tr className="small text-uppercase" style={{ letterSpacing: '0.5px' }}>
                                        <th className="py-2 px-3 text-white border-0">{t('col_customer', 'PDV / Comercio')}</th>
                                        <th className="py-2 text-white border-0">{t('col_zone', 'Zona')}</th>
                                        <th className="py-2 text-white border-0">{t('col_merchandiser', 'Mercaderista')}</th>
                                        <th className="py-2 text-white border-0">{t('col_date', 'Fecha')}</th>
                                        {drillDownModal.type === 'pop' && (
                                            <th className="py-2 text-white border-0">{t('drilldown_photo_evidence', 'Evidencia')}</th>
                                        )}
                                        {drillDownModal.type === 'margin' && (
                                            <th className="py-2 text-white border-0">{t('label_margin', 'Margen')}</th>
                                        )}
                                        <th className="py-2 px-3 text-end text-white border-0">{t('col_actions', 'Acción')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {drillDownModal.items.map((it, idx) => (
                                        <tr key={idx} style={{ borderBottom: `1px solid ${cardBorder}` }}>
                                            <td className="px-3">
                                                <div className="fw-bold small" style={{ color: isDark ? '#ffffff' : BRAND_NAVY }}>
                                                    {it.cliente || it.nombre_comercial || 'Comercio'}
                                                </div>
                                                {it.rif && (
                                                    <span className="small text-muted font-monospace" style={{ fontSize: '0.74rem' }}>
                                                        {it.rif}
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <span 
                                                    className="badge rounded-pill fw-normal" 
                                                    style={{ backgroundColor: subCardBg, color: textColor, border: `1px solid ${cardBorder}`, fontSize: '0.74rem' }}
                                                >
                                                    <i className="mdi mdi-map-marker-outline me-1 text-info"></i>
                                                    {it.zona || 'Sin Zona'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="small d-flex align-items-center gap-1">
                                                    <i className="mdi mdi-account-tie text-primary"></i>
                                                    <span>{it.vendedor || 'Mercaderista N/A'}</span>
                                                </div>
                                            </td>
                                            <td className="small text-muted">
                                                {it.fecha ? formatVisitDate(it.fecha) : '-'}
                                            </td>

                                            {/* Specialized Column based on drilldown type */}
                                            {drillDownModal.type === 'pop' && (
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        {(it.foto_antes_url || it.foto_despues_url) ? (
                                                            <div 
                                                                role="button"
                                                                className="rounded border overflow-hidden position-relative"
                                                                style={{ width: '42px', height: '42px', cursor: 'pointer', borderColor: cardBorder }}
                                                                onClick={() => setZoomImage({ 
                                                                    url: it.foto_antes_url || it.foto_despues_url, 
                                                                    title: `${it.cliente || 'PDV'} - ${it.estado_exhibidor || 'POP'}` 
                                                                })}
                                                                title={t('btn_zoom', 'Ampliar Foto')}
                                                            >
                                                                <img 
                                                                    src={it.foto_antes_url || it.foto_despues_url} 
                                                                    alt="POP" 
                                                                    className="w-100 h-100" 
                                                                    style={{ objectFit: 'cover' }} 
                                                                />
                                                                <div 
                                                                    className="position-absolute bottom-0 end-0 bg-dark text-white d-flex align-items-center justify-content-center"
                                                                    style={{ width: '16px', height: '16px', opacity: 0.8 }}
                                                                >
                                                                    <i className="mdi mdi-magnify" style={{ fontSize: '10px' }}></i>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <span className="small text-muted">{t('no_photo', 'Sin foto')}</span>
                                                        )}
                                                        {it.observacion && (
                                                            <span className="small text-muted text-truncate" style={{ maxWidth: '120px' }} title={it.observacion}>
                                                                {it.observacion}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                            )}

                                            {drillDownModal.type === 'margin' && (
                                                <td>
                                                    {renderMarginBadge(it.margen_percibido)}
                                                </td>
                                            )}

                                            <td className="px-3 text-end">
                                                <Button
                                                    size="sm"
                                                    variant="primary"
                                                    className="py-1 px-2 fw-semibold d-inline-flex align-items-center gap-1 shadow-sm"
                                                    style={{ borderRadius: '6px', fontSize: '0.78rem', backgroundColor: BRAND_BLUE, borderColor: BRAND_BLUE }}
                                                    onClick={() => handleOpenReportById(it.report_id)}
                                                    title={t('drilldown_view_audit', 'Ver Auditoría Completa')}
                                                >
                                                    <i className="mdi mdi-file-document-outline"></i>
                                                    {t('btn_audit', 'Auditoría')}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="py-5 text-center text-muted">
                            <i className="mdi mdi-store-search-outline fs-1 mb-2 d-block opacity-50"></i>
                            <h6 className="fw-semibold">{t('drilldown_no_records', 'No se encontraron registros específicos para esta categoría.')}</h6>
                            <p className="small mb-0">{t('drilldown_empty_desc', 'Verifique los filtros de fecha, zona o mercaderista seleccionados.')}</p>
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer className="d-flex justify-content-between align-items-center" style={{ backgroundColor: subCardBg, borderColor: cardBorder }}>
                    <div className="small text-muted">
                        <strong>Total:</strong> {drillDownModal.items?.length || 0} {t('records_label', 'comercios relevados')}
                    </div>
                    <Button 
                        variant="secondary" 
                        size="sm" 
                        onClick={() => setDrillDownModal(prev => ({ ...prev, show: false }))} 
                        style={{ borderRadius: '6px' }}
                    >
                        {t('btn_close', 'Cerrar')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* FULL AUDIT DETAIL MODAL (RUTEX CORPORATE MODAL IDENTITY) */}
            <Modal 
                show={showDetailModal} 
                onHide={() => setShowDetailModal(false)} 
                size="xl" 
                centered 
                scrollable
                style={{ zIndex: 1060 }}
            >
                <Modal.Header 
                    closeButton 
                    closeVariant="white"
                    style={{ backgroundColor: BRAND_NAVY, color: '#ffffff', borderBottom: `3px solid ${BRAND_BLUE}` }}
                >
                    <Modal.Title style={{ color: '#ffffff', fontWeight: 'bold', fontSize: '1.15rem' }}>
                        <i className="mdi mdi-store-check-outline me-2 text-primary"></i>
                        {t('modal_audit_title', 'Auditoría de Visita')}: {selectedReport?.visit?.cliente?.nombre_comercial || selectedReport?.visit?.cliente?.razon_social}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="p-4" style={{ backgroundColor: cardBg, color: textColor }}>
                    {selectedReport && (
                        <div>
                            {/* Summary Header Strip */}
                            <div className="p-3 rounded mb-4 border" style={{ backgroundColor: subCardBg, borderColor: cardBorder }}>
                                <Row className="g-3">
                                    <Col xs={12} md={4}>
                                        <span className="small text-muted d-block">{t('label_pdv', 'Punto de Venta / RIF:')}</span>
                                        <strong className="fs-6" style={{ color: textColor }}>
                                            {selectedReport.visit?.cliente?.nombre_comercial || selectedReport.visit?.cliente?.razon_social}
                                        </strong>
                                        <div className="small text-muted text-truncate">{selectedReport.visit?.cliente?.rif_cedula} • {selectedReport.visit?.cliente?.direccion_fisica}</div>
                                    </Col>
                                    <Col xs={6} md={3}>
                                        <span className="small text-muted d-block">{t('label_merchandiser', 'Mercaderista:')}</span>
                                        <strong className="d-flex align-items-center gap-1" style={{ color: textColor }}>
                                            <i className="mdi mdi-account-tie text-primary"></i>
                                            {selectedReport.vendedor?.nombre_completo || t('unassigned', 'No asignado')}
                                        </strong>
                                        <div className="small text-muted text-truncate">{selectedReport.vendedor?.email}</div>
                                    </Col>
                                    <Col xs={6} md={2}>
                                        <span className="small text-muted d-block">{t('label_zone_route', 'Zona / Ruta:')}</span>
                                        <strong className="d-flex align-items-center gap-1" style={{ color: textColor }}>
                                            <i className="mdi mdi-map-marker-outline text-info"></i>
                                            {selectedReport.visit?.cliente?.zone?.name || t('unassigned', 'Zona N/A')}
                                        </strong>
                                        <div className="small text-muted text-truncate">{selectedReport.visit?.route?.nombre_ruta || t('standard_route', 'Ruta estándar')}</div>
                                    </Col>
                                    <Col xs={12} md={3} className="text-md-end">
                                        <span className="small text-muted d-block">{t('label_visit_datetime', 'Fecha y Horario:')}</span>
                                        <strong style={{ color: textColor }}>
                                            <i className="mdi mdi-calendar-blank-outline me-1 text-primary"></i>
                                            {formatVisitDate(selectedReport.visit?.fecha_ruta || selectedReport.created_at)}
                                        </strong>
                                        <div className="small text-muted">
                                            {selectedReport.visit?.check_in_time ? formatVisitTime(selectedReport.visit.check_in_time) : '-'} 
                                            {' ➔ '}
                                            {selectedReport.visit?.check_out_time ? formatVisitTime(selectedReport.visit.check_out_time) : '-'}
                                        </div>
                                    </Col>
                                </Row>
                            </div>

                            {/* Section 1: Photos Before and After */}
                            <div className="mb-4">
                                <h6 className="fw-bold d-flex align-items-center gap-2 mb-3 pb-2 border-bottom" style={{ borderColor: cardBorder, color: isDark ? '#ffffff' : BRAND_NAVY }}>
                                    <i className="mdi mdi-camera-burst text-primary fs-5"></i>
                                    {t('section_shelf_display', '1. Estado del Anaquel y Exhibición (Antes vs. Después)')}
                                </h6>
                                <Row className="g-3">
                                    <Col xs={12} md={6}>
                                        <div className="p-3 rounded border text-center" style={{ backgroundColor: subCardBg, borderColor: cardBorder }}>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span className="badge bg-dark px-3 py-1">{t('photo_before_badge', 'FOTO ANTES')}</span>
                                                {selectedReport.foto_antes_url && (
                                                    <Button variant="link" size="sm" className="p-0 text-primary" onClick={() => setZoomImage({ url: selectedReport.foto_antes_url, title: t('photo_before_title', 'Foto Antes del Anaquel') })}>
                                                        <i className="mdi mdi-magnify-plus me-1"></i>{t('btn_zoom', 'Ampliar')}
                                                    </Button>
                                                )}
                                            </div>
                                            {selectedReport.foto_antes_url ? (
                                                <div 
                                                    className="rounded overflow-hidden border" 
                                                    style={{ height: '260px', cursor: 'pointer', borderColor: cardBorder }}
                                                    onClick={() => setZoomImage({ url: selectedReport.foto_antes_url, title: t('photo_before_title', 'Foto Antes del Anaquel') })}
                                                >
                                                    <img 
                                                        src={selectedReport.foto_antes_url} 
                                                        alt="Anaquel Antes" 
                                                        className="w-100 h-100" 
                                                        style={{ objectFit: 'contain', backgroundColor: isDark ? '#080d1a' : '#f8f9fa' }} 
                                                    />
                                                </div>
                                            ) : (
                                                <div className="p-5 text-muted border rounded" style={{ height: '260px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                                    <i className="mdi mdi-camera-off fs-1 mb-2"></i>
                                                    <span>{t('no_photo_before', 'No se subió foto previa')}</span>
                                                </div>
                                            )}
                                        </div>
                                    </Col>

                                    <Col xs={12} md={6}>
                                        <div className="p-3 rounded border text-center" style={{ backgroundColor: subCardBg, borderColor: cardBorder }}>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span className="badge bg-success px-3 py-1">{t('photo_after_badge', 'FOTO DESPUÉS (ORDENADO)')}</span>
                                                {selectedReport.foto_despues_url && (
                                                    <Button variant="link" size="sm" className="p-0 text-success" onClick={() => setZoomImage({ url: selectedReport.foto_despues_url, title: t('photo_after_title', 'Foto Después del Anaquel') })}>
                                                        <i className="mdi mdi-magnify-plus me-1"></i>{t('btn_zoom', 'Ampliar')}
                                                    </Button>
                                                )}
                                            </div>
                                            {selectedReport.foto_despues_url ? (
                                                <div 
                                                    className="rounded overflow-hidden border" 
                                                    style={{ height: '260px', cursor: 'pointer', borderColor: cardBorder }}
                                                    onClick={() => setZoomImage({ url: selectedReport.foto_despues_url, title: t('photo_after_title', 'Foto Después del Anaquel') })}
                                                >
                                                    <img 
                                                        src={selectedReport.foto_despues_url} 
                                                        alt="Anaquel Después" 
                                                        className="w-100 h-100" 
                                                        style={{ objectFit: 'contain', backgroundColor: isDark ? '#080d1a' : '#f8f9fa' }} 
                                                    />
                                                </div>
                                            ) : (
                                                <div className="p-5 text-muted border rounded" style={{ height: '260px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                                    <i className="mdi mdi-camera-off fs-1 mb-2"></i>
                                                    <span>{t('no_photo_after', 'No se subió foto posterior')}</span>
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            </div>

                            {/* Section 2: Margin Perception & Hotspots Evaluation */}
                            <div className="mb-4">
                                <h6 className="fw-bold d-flex align-items-center gap-2 mb-3 pb-2 border-bottom" style={{ borderColor: cardBorder, color: isDark ? '#ffffff' : BRAND_NAVY }}>
                                    <i className="mdi mdi-chart-areaspline text-info fs-5"></i>
                                    {t('section_margin_hotspots', '2. Percepción de Margen y Ubicación Estratégica')}
                                </h6>
                                <Row className="g-3">
                                    {/* Perceived Margin */}
                                    <Col xs={12} md={5}>
                                        <div className="p-3 rounded border h-100" style={{ backgroundColor: subCardBg, borderColor: cardBorder }}>
                                            <span className="small text-muted d-block mb-1">{t('margin_evaluation_title', 'Margen Percibido del Cliente:')}</span>
                                            <div className="d-flex align-items-center gap-3 mb-2">
                                                {renderMarginBadge(selectedReport.margen_percibido)}
                                                <span className="small fw-semibold" style={{ color: textColor }}>
                                                    {selectedReport.margen_percibido === 'Alto' && t('margin_high_desc', 'Excelente rentabilidad')}
                                                    {selectedReport.margen_percibido === 'En el rango' && t('margin_range_desc', 'Dentro de expectativas')}
                                                    {selectedReport.margen_percibido === 'Bajo' && t('margin_low_desc', 'Comercio percibe margen ajustado')}
                                                    {!selectedReport.margen_percibido && t('not_evaluated', 'No evaluado')}
                                                </span>
                                            </div>
                                            <p className="small text-muted mb-0" style={{ fontSize: '0.8rem' }}>
                                                {t('margin_help_text', 'Métrica comercial que refleja la rentabilidad percibida por el comercio.')}
                                            </p>
                                        </div>
                                    </Col>

                                    {/* Hotspots Presence Grid */}
                                    <Col xs={12} md={7}>
                                        <div className="p-3 rounded border h-100" style={{ backgroundColor: subCardBg, borderColor: cardBorder }}>
                                            <span className="small text-muted d-block mb-2">{t('hotspots_presence_title', 'Presencia en Puntos Calientes (Zonas de Alto Tráfico):')}</span>
                                            <Row className="g-2">
                                                {HOTSPOT_CONFIG.map(spot => {
                                                    const isActive = hasHotspot(selectedReport.puntos_calientes, spot.key);
                                                    return (
                                                        <Col xs={6} key={spot.key}>
                                                            <div 
                                                                className={`p-2 rounded border d-flex align-items-center gap-2 ${isActive ? 'bg-success bg-opacity-10 border-success' : 'opacity-75'}`}
                                                                style={{ 
                                                                    borderColor: isActive ? '#059669' : cardBorder,
                                                                    backgroundColor: isActive ? (isDark ? 'rgba(5, 150, 105, 0.15)' : '#ecfdf5') : 'transparent' 
                                                                }}
                                                            >
                                                                <i className={`mdi ${isActive ? 'mdi-check-circle text-success' : 'mdi-close-circle-outline text-muted'} fs-5`}></i>
                                                                <div>
                                                                    <div className={`small fw-bold ${isActive ? 'text-success' : 'text-muted'}`} style={{ fontSize: '0.8rem' }}>
                                                                        {spot.label}
                                                                    </div>
                                                                    <span style={{ fontSize: '0.7rem' }} className="text-muted">
                                                                        {isActive ? t('active_spot', 'Presente') : t('inactive_spot', 'No ocupado')}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    );
                                                })}
                                            </Row>
                                        </div>
                                    </Col>
                                </Row>
                            </div>

                            {/* Section 3: Material POP */}
                            <div className="mb-4">
                                <h6 className="fw-bold d-flex align-items-center gap-2 mb-3 pb-2 border-bottom" style={{ borderColor: cardBorder, color: isDark ? '#ffffff' : BRAND_NAVY }}>
                                    <i className="mdi mdi-store-check text-success fs-5"></i>
                                    {t('section_pop_materials', '3. Evaluación de Material POP')}
                                </h6>
                                <Row className="g-3">
                                    <Col xs={12} sm={4}>
                                        <div className="p-3 rounded border text-center" style={{ backgroundColor: subCardBg, borderColor: cardBorder }}>
                                            <i className="mdi mdi-store-24-hour fs-3 text-primary mb-1"></i>
                                            <div className="small fw-bold mb-1" style={{ color: textColor }}>{t('pop_main_display', 'Exhibidor Principal')}</div>
                                            <div>{renderPopBadge(selectedReport.material_pop)}</div>
                                        </div>
                                    </Col>
                                    <Col xs={12} sm={4}>
                                        <div className="p-3 rounded border text-center" style={{ backgroundColor: subCardBg, borderColor: cardBorder }}>
                                            <i className="mdi mdi-tag-text-outline fs-3 text-info mb-1"></i>
                                            <div className="small fw-bold mb-1" style={{ color: textColor }}>{t('pop_talkers', 'Habladores de Góndola')}</div>
                                            <div>
                                                {selectedReport.material_pop?.habladores === 'buen_estado' ? (
                                                    <span className="badge rounded-pill" style={{ backgroundColor: isDark ? 'rgba(16, 185, 129, 0.22)' : 'rgba(5, 150, 105, 0.15)', color: isDark ? '#6ee7b7' : '#059669', border: isDark ? '1px solid rgba(110, 231, 183, 0.4)' : '1px solid rgba(5, 150, 105, 0.3)', fontWeight: '600' }}><i className="mdi mdi-check-circle-outline me-1"></i>Buen Estado</span>
                                                ) : selectedReport.material_pop?.habladores === 'danado' ? (
                                                    <span className="badge rounded-pill" style={{ backgroundColor: isDark ? 'rgba(239, 68, 68, 0.22)' : 'rgba(239, 68, 68, 0.15)', color: isDark ? '#fca5a5' : '#dc2626', border: isDark ? '1px solid rgba(252, 165, 165, 0.4)' : '1px solid rgba(239, 68, 68, 0.3)', fontWeight: '600' }}><i className="mdi mdi-alert-circle-outline me-1"></i>Dañado</span>
                                                ) : (
                                                    <span className="badge rounded-pill" style={{ backgroundColor: isDark ? 'rgba(148, 163, 184, 0.2)' : 'rgba(100, 116, 139, 0.15)', color: isDark ? '#cbd5e1' : '#64748b', border: isDark ? '1px solid rgba(148, 163, 184, 0.4)' : '1px solid rgba(100, 116, 139, 0.3)', fontWeight: '500' }}>Ausente / S/D</span>
                                                )}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col xs={12} sm={4}>
                                        <div className="p-3 rounded border text-center" style={{ backgroundColor: subCardBg, borderColor: cardBorder }}>
                                            <i className="mdi mdi-newspaper-variant-outline fs-3 mb-1" style={{ color: '#8b5cf6' }}></i>
                                            <div className="small fw-bold mb-1" style={{ color: textColor }}>{t('pop_posters', 'Afiches Promocionales')}</div>
                                            <div>
                                                {selectedReport.material_pop?.afiches === 'buen_estado' ? (
                                                    <span className="badge rounded-pill" style={{ backgroundColor: isDark ? 'rgba(16, 185, 129, 0.22)' : 'rgba(5, 150, 105, 0.15)', color: isDark ? '#6ee7b7' : '#059669', border: isDark ? '1px solid rgba(110, 231, 183, 0.4)' : '1px solid rgba(5, 150, 105, 0.3)', fontWeight: '600' }}><i className="mdi mdi-check-circle-outline me-1"></i>Buen Estado</span>
                                                ) : selectedReport.material_pop?.afiches === 'danado' ? (
                                                    <span className="badge rounded-pill" style={{ backgroundColor: isDark ? 'rgba(239, 68, 68, 0.22)' : 'rgba(239, 68, 68, 0.15)', color: isDark ? '#fca5a5' : '#dc2626', border: isDark ? '1px solid rgba(252, 165, 165, 0.4)' : '1px solid rgba(239, 68, 68, 0.3)', fontWeight: '600' }}><i className="mdi mdi-alert-circle-outline me-1"></i>Dañado</span>
                                                ) : (
                                                    <span className="badge rounded-pill" style={{ backgroundColor: isDark ? 'rgba(148, 163, 184, 0.2)' : 'rgba(100, 116, 139, 0.15)', color: isDark ? '#cbd5e1' : '#64748b', border: isDark ? '1px solid rgba(148, 163, 184, 0.4)' : '1px solid rgba(100, 116, 139, 0.3)', fontWeight: '500' }}>Ausente / S/D</span>
                                                )}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                                {selectedReport.material_pop?.observacion && (
                                    <div className="mt-2 p-2 rounded small border text-muted" style={{ backgroundColor: subCardBg, borderColor: cardBorder }}>
                                        <strong>{t('pop_observations_label', 'Observaciones de exhibición / POP:')}</strong> {selectedReport.material_pop.observacion}
                                    </div>
                                )}
                            </div>

                            {/* Section 4: Current In-Store Inventory & Retail PVP */}
                            <div className="mb-4">
                                <div className="d-flex flex-wrap justify-content-between align-items-center pb-2 mb-3 border-bottom" style={{ borderColor: cardBorder }}>
                                    <h6 className="fw-bold d-flex align-items-center gap-2 mb-0" style={{ color: isDark ? '#ffffff' : BRAND_NAVY }}>
                                        <i className="mdi mdi-package-variant-closed text-primary fs-5"></i>
                                        {t('section_inventory_pvp', '4. Inventario Actual en Tienda y PVP al Consumidor')}
                                    </h6>
                                    <span className="small text-muted">
                                        <i className="mdi mdi-information-outline me-1"></i>
                                        {t('inventory_pvp_hint', 'Conteo de existencia física en tienda y precio de venta al consumidor registrado')}
                                    </span>
                                </div>

                                {Array.isArray(selectedReport.inventario_pdv) && selectedReport.inventario_pdv.length > 0 ? (
                                    <div className="table-responsive rounded border" style={{ borderColor: cardBorder }}>
                                        <Table className="mb-0 table-sm align-middle" style={{ color: textColor }}>
                                            <thead style={{ backgroundColor: BRAND_BLUE, color: '#ffffff' }}>
                                                <tr className="small text-uppercase" style={{ letterSpacing: '0.5px' }}>
                                                    <th className="py-2 px-3 text-white border-0" style={{ width: '130px' }}>{t('col_sku', 'SKU')}</th>
                                                    <th className="py-2 text-white border-0">{t('col_product', 'Producto / Presentación')}</th>
                                                    <th className="py-2 text-center text-white border-0" style={{ width: '180px' }}>{t('col_stock', 'Existencia / Stock')}</th>
                                                    <th className="py-2 text-end px-4 text-white border-0" style={{ width: '200px' }}>{t('col_retail_pvp', 'PVP al Consumidor ($ USD)')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedReport.inventario_pdv.map((inv, idx) => {
                                                    const retailPvp = inv.pvp_usd ? Number(inv.pvp_usd) : null;

                                                    return (
                                                        <tr key={idx} style={{ borderBottom: `1px solid ${cardBorder}` }}>
                                                            <td className="px-3">
                                                                <Badge bg="dark" className="font-monospace fw-normal text-light px-2 py-1" style={{ fontSize: '0.75rem' }}>
                                                                    {inv.sku || 'S/SKU'}
                                                                </Badge>
                                                            </td>
                                                            <td>
                                                                <div className="fw-semibold text-truncate" style={{ maxWidth: '400px' }} title={inv.producto}>
                                                                    {inv.producto || 'Producto'}
                                                                </div>
                                                                {inv.marca && <span className="small text-muted">Marca: {inv.marca}</span>}
                                                            </td>
                                                            <td className="text-center">
                                                                <Badge bg="primary" className="px-2 py-1 fs-6">
                                                                    {inv.cantidad}
                                                                </Badge>
                                                                <span className="text-muted small ms-1">{inv.unidad || 'uds'}</span>
                                                            </td>
                                                            <td className="text-end px-4 fw-bold text-success" style={{ fontSize: '0.95rem' }}>
                                                                {retailPvp ? `$ ${retailPvp.toFixed(2)}` : '-'}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                ) : (
                                    <div className="p-3 text-muted small border rounded" style={{ backgroundColor: subCardBg, borderColor: cardBorder }}>
                                        {t('no_initial_inventory', 'No se reportó conteo de inventario en esta visita.')}
                                    </div>
                                )}
                            </div>

                            {/* Section 5 & 6: Competitor Prices & In-Store Support Tasks */}
                            <Row className="g-4 mb-4">
                                {/* Competitor Intelligence */}
                                <Col xs={12} md={6}>
                                    <h6 className="fw-bold d-flex align-items-center gap-2 mb-3 pb-2 border-bottom" style={{ borderColor: cardBorder, color: isDark ? '#ffffff' : BRAND_NAVY }}>
                                        <i className="mdi mdi-chart-line text-warning fs-5"></i>
                                        {t('section_competitor_prices', '5. Precios de la Competencia (Benchmarking)')}
                                    </h6>
                                    {Array.isArray(selectedReport.precios_competencia) && selectedReport.precios_competencia.length > 0 ? (
                                        <div className="table-responsive rounded border" style={{ borderColor: cardBorder }}>
                                            <Table className="mb-0 table-sm align-middle" style={{ color: textColor }}>
                                                <thead style={{ backgroundColor: BRAND_BLUE, color: '#ffffff' }}>
                                                    <tr className="small text-uppercase" style={{ letterSpacing: '0.5px' }}>
                                                        <th className="py-2 px-3 text-white border-0">{t('col_rival_brand', 'Marca Rival')}</th>
                                                        <th className="py-2 text-white border-0">{t('col_product', 'Producto')}</th>
                                                        <th className="py-2 text-end px-3 text-white border-0">{t('col_competitor_price', 'Precio ($ USD)')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedReport.precios_competencia.map((comp, idx) => (
                                                        <tr key={idx} style={{ borderBottom: `1px solid ${cardBorder}` }}>
                                                            <td className="px-3 fw-bold">{comp.marca || comp.competidor || 'Competidor'}</td>
                                                            <td className="small">{comp.producto || '-'}</td>
                                                            <td className="text-end px-3 fw-bold text-success font-monospace">
                                                                {comp.precio_usd ? `$ ${Number(comp.precio_usd).toFixed(2)}` : (comp.precio ? `$ ${Number(comp.precio).toFixed(2)}` : '-')}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    ) : (
                                        <div className="p-3 text-muted small border rounded" style={{ backgroundColor: subCardBg, borderColor: cardBorder }}>
                                            {t('no_competitor_prices', 'No se reportaron precios de competencia en esta visita.')}
                                        </div>
                                    )}
                                </Col>

                                {/* Support Tasks / Operational Notes */}
                                <Col xs={12} md={6}>
                                    <h6 className="fw-bold d-flex align-items-center gap-2 mb-3 pb-2 border-bottom" style={{ borderColor: cardBorder, color: isDark ? '#ffffff' : BRAND_NAVY }}>
                                        <i className="mdi mdi-clipboard-text-outline text-success fs-5"></i>
                                        {t('section_support_notes', '6. Labores de Apoyo en Tienda / Notas')}
                                    </h6>
                                    {(() => {
                                        let go = selectedReport.gestion_operativa;
                                        if (!go) {
                                            return (
                                                <div className="p-3 text-muted small border rounded" style={{ backgroundColor: subCardBg, borderColor: cardBorder }}>
                                                    {t('no_support_notes', 'Sin notas adicionales de labores en tienda.')}
                                                </div>
                                            );
                                        }

                                        if (typeof go === 'string' && (go.trim().startsWith('{') || go.trim().startsWith('['))) {
                                            try {
                                                go = JSON.parse(go);
                                            } catch (e) {
                                                // Keep as string
                                            }
                                        }

                                        if (typeof go === 'string') {
                                            return (
                                                <div className="p-3 rounded border" style={{ backgroundColor: subCardBg, borderColor: cardBorder }}>
                                                    <p className="mb-0 small">{go}</p>
                                                </div>
                                            );
                                        }

                                        if (typeof go === 'object' && !Array.isArray(go)) {
                                            const tasks = Array.isArray(go.tasks) ? go.tasks : (Array.isArray(go.labores) ? go.labores : []);
                                            const notas = go.notas || go.observaciones || go.nota || '';

                                            if (tasks.length === 0 && !notas) {
                                                return (
                                                    <div className="p-3 text-muted small border rounded" style={{ backgroundColor: subCardBg, borderColor: cardBorder }}>
                                                        {t('no_support_notes', 'Sin notas adicionales de labores en tienda.')}
                                                    </div>
                                                );
                                            }

                                            return (
                                                <div className="p-3 rounded border" style={{ backgroundColor: subCardBg, borderColor: cardBorder }}>
                                                    {tasks.length > 0 && (
                                                        <div className="mb-2">
                                                            <small className="text-muted d-block mb-2 fw-semibold">{t('support_tasks_executed', 'Labores Realizadas:')}</small>
                                                            <div className="d-flex flex-wrap gap-2">
                                                                {tasks.map((task, idx) => (
                                                                    <Badge key={idx} bg="primary" className="px-2 py-1 d-inline-flex align-items-center" style={{ fontSize: '0.8rem', fontWeight: 500, backgroundColor: '#183eeb' }}>
                                                                        <i className="mdi mdi-check-circle-outline me-1"></i>
                                                                        {task}
                                                                    </Badge>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {notas && (
                                                        <div className={tasks.length > 0 ? "pt-2 mt-2 border-top" : ""} style={{ borderColor: cardBorder }}>
                                                            <small className="text-muted d-block mb-1 fw-semibold">{t('operational_notes_label', 'Notas / Observaciones:')}</small>
                                                            <p className="mb-0 small" style={{ whiteSpace: 'pre-line' }}>{notas}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        }

                                        if (Array.isArray(go) && go.length > 0 && typeof go[0] === 'string') {
                                            return (
                                                <div className="p-3 rounded border" style={{ backgroundColor: subCardBg, borderColor: cardBorder }}>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {go.map((task, idx) => (
                                                            <Badge key={idx} bg="primary" className="px-2 py-1 d-inline-flex align-items-center" style={{ fontSize: '0.8rem', fontWeight: 500, backgroundColor: '#183eeb' }}>
                                                                <i className="mdi mdi-check-circle-outline me-1"></i>
                                                                {task}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        }

                                        if (Array.isArray(go) && go.length > 0) {
                                            return (
                                                <div className="table-responsive rounded border" style={{ borderColor: cardBorder }}>
                                                    <Table className="mb-0 table-sm align-middle" style={{ color: textColor }}>
                                                        <thead style={{ backgroundColor: BRAND_BLUE, color: '#ffffff' }}>
                                                            <tr className="small text-uppercase" style={{ letterSpacing: '0.5px' }}>
                                                                <th className="py-2 px-3 text-white border-0">{t('col_task_detail', 'Labor / Detalle')}</th>
                                                                <th className="py-2 text-white border-0">{t('col_product', 'Producto')}</th>
                                                                <th className="py-2 text-end px-3 text-white border-0">{t('col_quantity', 'Cantidad')}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {go.map((gest, idx) => (
                                                                <tr key={idx} style={{ borderBottom: `1px solid ${cardBorder}` }}>
                                                                    <td className="px-3">
                                                                        <span className="small text-capitalize">
                                                                            {(gest.actividad || 'rebanado').replace('_', ' ')}
                                                                        </span>
                                                                    </td>
                                                                    <td className="fw-semibold small">{gest.producto || '-'}</td>
                                                                    <td className="text-end px-3 small text-muted">
                                                                        {gest.cantidad} {gest.unidad || 'bandejas'}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            );
                                        }

                                        return (
                                            <div className="p-3 text-muted small border rounded" style={{ backgroundColor: subCardBg, borderColor: cardBorder }}>
                                                {t('no_support_notes', 'Sin notas adicionales de labores en tienda.')}
                                            </div>
                                        );
                                    })()}
                                </Col>
                            </Row>

                            {/* Section 7: Out of stock checklist */}
                            {Array.isArray(selectedReport.quiebres_stock) && selectedReport.quiebres_stock.length > 0 && (
                                <div className="mb-4">
                                    <div className="d-flex flex-wrap justify-content-between align-items-center pb-2 mb-3 border-bottom" style={{ borderColor: cardBorder }}>
                                        <h6 className="fw-bold d-flex align-items-center gap-2 mb-0 text-danger">
                                            <i className="mdi mdi-alert-circle-outline fs-5"></i>
                                            {t('section_out_of_stock_title', '7. Quiebres de Stock Detectados')} ({selectedReport.quiebres_stock.length} {t('out_of_stock_tag', 'agotados')})
                                        </h6>
                                        <span className="small text-muted">
                                            {t('out_of_stock_hint', 'Productos marcados en el checklist sin existencia en anaquel')}
                                        </span>
                                    </div>

                                    <Row className="g-3">
                                        {selectedReport.quiebres_stock.map((q, idx) => (
                                            <Col xs={12} md={6} key={idx}>
                                                <div 
                                                    className="p-3 rounded-4 d-flex align-items-center justify-content-between gap-3 border shadow-sm"
                                                    style={{ 
                                                        borderColor: isDark ? '#b91c1c' : '#f87171', 
                                                        borderWidth: '1.5px',
                                                        backgroundColor: isDark ? 'rgba(239, 68, 68, 0.08)' : '#ffffff' 
                                                    }}
                                                >
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div 
                                                            className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                                                            style={{ 
                                                                width: '52px', 
                                                                height: '52px', 
                                                                backgroundColor: isDark ? '#1e293b' : '#eef2ff',
                                                                border: isDark ? '1px solid #334155' : '1px solid #dbeafe'
                                                            }}
                                                        >
                                                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={BRAND_BLUE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M7 3l1.5 17a2 2 0 0 0 2 2h3a2 2 0 0 0 2-2L17 3H7z"></path>
                                                                <path d="M12 11c-1 1.2-1.5 2-1.5 2.8a1.5 1.5 0 0 0 3 0c0-.8-.5-1.6-1.5-2.8z" fill={BRAND_BLUE}></path>
                                                            </svg>
                                                        </div>
                                                        <div>
                                                            <div className="d-flex align-items-center gap-2 mb-1">
                                                                <span 
                                                                    className="badge text-uppercase fw-semibold px-2 py-1"
                                                                    style={{ 
                                                                        backgroundColor: isDark ? '#334155' : '#e2e8f0', 
                                                                        color: isDark ? '#f1f5f9' : '#334155', 
                                                                        fontSize: '0.68rem',
                                                                        borderRadius: '6px',
                                                                        letterSpacing: '0.5px'
                                                                    }}
                                                                >
                                                                    {q.categoria || t('product_category_default', 'PRODUCTO')}
                                                                </span>
                                                                <span className="text-muted font-monospace" style={{ fontSize: '0.8rem' }}>
                                                                    SKU: {q.sku || 'N/A'}
                                                                </span>
                                                            </div>
                                                            <div className="fw-bold" style={{ color: textColor, fontSize: '1.02rem', lineHeight: 1.25 }}>
                                                                {q.nombre || q.producto || q.sku || t('product_out_of_stock_default', 'Producto Agotado')}
                                                            </div>
                                                            <div className="d-flex align-items-center gap-1 text-danger mt-1" style={{ fontSize: '0.84rem', fontWeight: 600 }}>
                                                                <i className="mdi mdi-close-circle-outline"></i>
                                                                <span>{t('shelf_out_of_stock', 'Agotado en anaquel')}</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div 
                                                        className="rounded-pill px-3 py-2 text-white d-flex align-items-center gap-1 text-nowrap flex-shrink-0 fw-semibold"
                                                        style={{ 
                                                            backgroundColor: '#b91c1c', 
                                                            fontSize: '0.82rem',
                                                            boxShadow: '0 2px 5px rgba(185, 28, 28, 0.25)'
                                                        }}
                                                    >
                                                        <i className="mdi mdi-cart-remove"></i>
                                                        <span>{t('badge_no_stock', 'Sin Stock')}</span>
                                                    </div>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer style={{ backgroundColor: subCardBg, borderColor: cardBorder }}>
                    <Button variant="secondary" size="sm" onClick={() => setShowDetailModal(false)} style={{ borderRadius: '6px' }}>
                        {t('btn_close', 'Cerrar')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Styles for Lightbox / Zoom Modal Stacking over Detail & Drilldown Modals */}
            <style>
                {`
                    .zoom-image-modal {
                        z-index: 1080 !important;
                    }
                    .zoom-image-modal-backdrop {
                        z-index: 1070 !important;
                    }
                `}
            </style>

            {/* Lightbox / Zoom Modal (Stacked cleanly on top of Detail & Drilldown Modals) */}
            <Modal 
                show={!!zoomImage} 
                onHide={() => setZoomImage(null)} 
                size="lg" 
                centered
                className="zoom-image-modal"
                backdropClassName="zoom-image-modal-backdrop"
                style={{ zIndex: 1080 }}
                onExited={() => {
                    if (showDetailModal || drillDownModal.show) {
                        document.body.classList.add('modal-open');
                    }
                }}
            >
                <Modal.Header closeButton closeVariant={isDark ? "white" : undefined} style={{ backgroundColor: subCardBg, borderColor: cardBorder }}>
                    <Modal.Title className="fs-6 fw-bold">{zoomImage?.title || t('zoom_view', 'Vista Ampliada')}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-2 text-center" style={{ backgroundColor: isDark ? '#050a17' : '#000000' }}>
                    {zoomImage && (
                        <img 
                            src={zoomImage.url} 
                            alt="Zoom" 
                            className="img-fluid rounded" 
                            style={{ maxHeight: '80vh', objectFit: 'contain' }} 
                        />
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default MerchandisingReports;
